import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const LanguageContext = createContext(null);

// Translation files
const translations = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    skills: 'Skills',
    reading: 'Reading',
    writing: 'Writing',
    listening: 'Listening',
    speaking: 'Speaking',
    vocabulary: 'Vocabulary',
    grammar: 'Grammar',
    grammarPractice: 'Practice',
    grammarPageLead: 'Learn one form, then mix them. Mastery means choosing the right tense when they sit together.',
    grammarComingSoon: 'Coming soon',
    grammarItems: 'items',
    grammarCheck: 'Check answers',
    grammarScore: 'Score',
    grammarTryAgain: 'Try again',
    grammarBackToTopics: 'All topics',
    grammarHint: 'Hint',
    grammarExplanation: 'Why',
    grammarPerson: 'Person',
    grammarPositive: 'Positive',
    grammarNegative: 'Negative',
    grammarQuestion: 'Question',
    grammarGroup_present: 'Present',
    grammarGroup_past: 'Past',
    grammarGroup_future: 'Future',
    grammarGroup_articles: 'Articles',
    grammarGroup_agreement: 'Subject–verb agreement',
    grammarGroup_prepositions: 'Prepositions',
    grammarGroup_comparatives: 'Comparatives',
    grammarGroup_conditionals: 'Conditionals',
    grammarGroup_modals: 'Modals',
    grammarGroup_reported: 'Reported speech',
    grammarGroup_passives: 'Passive voice',
    grammarGroup_mastery: 'Mixed practice',
    grammarMastered: 'Mastered',
    grammarCompleted: 'Completed',
    grammarBest: 'Best',
    grammarAnswer: 'Answer',
    grammarRetryMissed: 'Practice missed',
    grammarRetryMissedHint: 'Only the items you missed. The others are kept.',
    grammarNextTopic: 'Next',
    grammarRecommended: 'Recommended next',
    grammarMasteryCount: '{n} of {total} completed',
    activities: 'Activities',
    profile: 'Profile',
    settings: 'Settings',
    login: 'Login',
    signup: 'Sign Up',
    logout: 'Logout',
    adminPanel: 'Admin',
    
    // Common
    welcome: 'Welcome',
    tryAgain: 'Try Again',
    
    // Dashboard
    welcomeBack: 'Welcome back',
    readyToContinue: 'Ready to continue your learning journey?',
    lessonsCompleted: 'Lessons Completed',
    timeSpent: 'Time Spent',
    averageScore: 'Average Score',
    learningStreak: 'Learning Streak',
    skillsProgress: 'Skills Progress',
    recentActivity: 'Recent Activity',
    noRecentActivity: 'No recent activity',
    minutes: 'min',
    days: 'days',
    loadingCosmicJourney: 'Loading your cosmic journey...',
    somethingWentWrong: 'Something went wrong',
    yourLearningDashboard: 'Your Learning Dashboard',
    continueCosmicJourney: 'Continue your cosmic learning journey and explore new frontiers of knowledge',
    trackYourMastery: 'Track your mastery across different skills',
    noSkillsDataAvailable: 'No skills data available',
    dashboardSubtitle: 'Your progress at a glance — step through and keep learning.',
    dashboardWeeklyActivity: 'This week',
    dashboardWeeklyActivityHint: 'Lessons completed each day',
    dashboardQuickPractice: 'Quick practice',
    dashboardQuickPracticeHint: 'Jump back into a skill',
    dashboardFocusArea: 'Focus area',
    dashboardCustomize: 'Customize',
    dashboardDailyGoal: "Today's goal",
    dashboardDailyGoalMet: 'Daily goal reached — great work!',
    dashboardDailyGoalHint: "{pct}% of today's goal complete",
    dashboardYourFocus: 'Your focus',
    dashboardFocusFromSettings: 'From your settings preference',
    dashboardFocusAutoWeakest: 'Suggested from your weakest skill',
    dashboardPickFocus: 'Choose a focus in Settings',
    dashboardFocusTag: 'Focus',
    dashboardStartedShort: 'started',
    dashboardRecentActivity: 'Recent activity',
    dashboardRecentActivityHint: 'Latest learning actions from your account',
    dashboardNoRecentActivity: 'No recent activity yet — start a lesson to begin.',
    dashboardStrongestSkill: 'Strongest skill',
    dashboardViewActivities: 'View all activity',
    dashboardLessonsThisWeek: 'Lessons this week',
    dashboardActiveDays: 'Active days',
    dashboardNoWeeklyData: 'No lessons completed this week yet. Start a practice session today.',
    dashboardPracticeNow: 'Practice now',
    dashboardOverallProgress: 'Overall progress',
    dashboardStreakActive: 'Keep your streak going',
    dashboardStreakStart: 'Start a lesson or review vocabulary to begin your streak',

    vocabPageLead: 'See how many words you have learned, what you still need to learn, and study each word with its meaning.',
    vocabFromThisLesson: 'Words from this lesson',
    vocabSelectHint: 'Tap a word in the passage, then save it to your bank.',
    vocabSaveWord: 'Save word',
    vocabSaving: 'Saving…',
    vocabSaved: 'Saved to your vocabulary',
    vocabSaveFailed: 'Could not save that word.',
    vocabAdd: 'Add',
    vocabDue: 'Due',
    vocabLearning: 'Learning',
    vocabLearned: 'Learned',
    vocabToLearn: 'To learn',
    vocabMastered: 'Mastered',
    vocabTotal: 'Saved',
    vocabRetention: 'Progress',
    vocabProgress: 'Learning progress',
    vocabProgressHint: '{learned} of {total} saved words learned',
    vocabDailyGoal: 'Daily reviews',
    vocabDailyGoalMet: 'Daily review goal met — nice work.',
    vocabGoalHint: '{done} of {goal} reviews today',
    vocabBySkill: 'Words by skill',
    vocabFilterAll: 'All skills',
    vocabTypeTheWord: 'Type the missing word',
    vocabCheck: 'Check',
    vocabRecallCorrect: 'You remembered it.',
    vocabRecallWrong: 'Not quite — the word is {word}',
    vocabRemove: 'Remove',
    vocabMarkLearned: 'I know this',
    vocabKeepLearning: 'Still learning',
    vocabMarkedLearned: 'Moved to Learned.',
    vocabMarkedLearning: 'Moved back to To learn.',
    vocabMarkFailed: 'Could not update this word. Please try again.',
    vocabStatus_new: 'New',
    vocabStatus_learning: 'Learning',
    vocabStatus_review: 'Review',
    vocabStatus_mastered: 'Mastered',
    vocabTab_review: 'Review',
    vocabTab_learning: 'To learn',
    vocabTab_learned: 'Learned',
    vocabTab_words: 'Word bank',
    vocabTab_quiz: 'Quiz',
    vocabCaughtUp: 'Nothing due right now.',
    vocabCaughtUpHint: 'Save words while you practice, then come back to review. Reviews also keep your streak.',
    vocabEmptyLearning: 'No words left to learn here. Save words from exercises, or check the Learned tab.',
    vocabEmptyLearned: 'Words you mark as learned will appear here with their meanings.',
    vocabEmptyBank: 'No saved words yet. Select a word in any exercise to start.',
    vocabShowMeaning: 'Show meaning',
    vocabNoMeaning: 'No meaning saved yet — add one from the exercise or edit later.',
    vocabAgain: 'Again',
    vocabHard: 'Hard',
    vocabGood: 'Good',
    vocabEasy: 'Easy',
    vocabRemaining: 'left in this session',
    vocabSearch: 'Search your words',
    vocabQuizLead: 'Match each word to its meaning. You need at least two saved words.',
    vocabStartQuiz: 'Start quiz',
    vocabNext: 'Next',
    vocabSeeScore: 'See score',
    vocabQuizScore: 'Score',
    dashboardVocabDue: '{n} due',
    dashboardVocabWords: 'words',
    dashboardVocabLearned: 'Words learned',
    dashboardVocabOfSaved: 'of {total} saved',
    dashboardVocabInProgress: '{n} still in progress',
    dashboardVocabNoneSaved: 'Save your first word',
    
    // Settings
    appearance: 'Appearance',
    language: 'Language',
    theme: 'Theme',
    lightMode: 'Light Mode',
    darkMode: 'Dark Mode',
    systemMode: 'System Mode',
    selectLanguage: 'Select Language',
    english: 'English',
    burmese: 'မြန်မာ',
    chinese: '中文',
    
    // Auth
    email: 'Email',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    confirmPassword: 'Confirm Password',
    firstName: 'First Name',
    lastName: 'Last Name',
    username: 'Username',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    createAccount: 'Create Account',
    creatingAccount: 'Creating Account',
    signingIn: 'Signing In',
    joinUsToGetStarted: 'Join us to get started',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: 'Don\'t have an account?',
    forgotPassword: 'Forgot password?',
    forgotPasswordLead: 'Enter the email for your account. If it is registered, we will send a reset link.',
    forgotPasswordSubmit: 'Send reset link',
    forgotPasswordSent: 'If that email is registered, you will receive a reset link shortly. Check your inbox.',
    sendingReset: 'Sending',
    backToSignIn: 'Back to sign in',
    resetPassword: 'Reset password',
    resetPasswordLead: 'Choose a new password for your account.',
    resetPasswordSubmit: 'Update password',
    resetPasswordSuccess: 'Your password has been updated. You can sign in now.',
    resetPasswordInvalid: 'This reset link is invalid or has expired. Request a new one.',
    passwordsDoNotMatch: 'Passwords do not match.',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    authLibraryPortal: 'Library portal',
    authNewRegistration: 'New registration',
    authWelcomeBack: 'Welcome back',
    authLoginTagline: 'Pick up your English journey in a calm, focused space.',
    authLoginFormHint: 'Sign in to continue learning at your pace.',
    authJoinHeadline: 'Begin your journey',
    authSignupTagline: 'A warm space to grow reading, writing, listening, and speaking.',
    authSignupFormHint: 'Create your free account — it only takes a moment.',
    dismiss: 'Dismiss',
    
    // Skills
    selectSkill: 'Select a skill',
    lessons: 'Lessons',
    difficulty: 'Difficulty',
    
    // Listening
    listeningPractice: 'Listening Practice',
    improveYourListeningSkills: 'Improve your listening skills',
    listenToTheAudioAndAnswerQuestions: 'Listen to the audio and answer the following questions',
    listeningChooseLevel: 'Choose Your Level',
    listeningPickShelf: 'Filter by level, then pick an audio lesson by title',
    listeningChooseLesson: 'Choose an audio lesson',
    listeningLesson: 'lesson',
    listeningLessons: 'lessons',
    listeningStartLesson: 'Start listening',
    listeningNoLessons: 'No listening lessons found for this level.',
    listeningBackToLessons: 'Back to lessons',
    listeningHasAudio: 'Audio',
    listeningVoiceOnly: 'Voice',
    listeningMin: 'min',
    listeningQuestionsCount: 'questions',
    listeningBooth: 'Listening booth',
    listeningNowPlaying: 'Now playing…',
    listeningPressPlay: 'Press play when you are ready',
    listeningAnswerHint: 'Listen carefully, then choose the best answer',
    listeningAnswered: 'answered',
    listeningAudioUnavailable: 'Audio unavailable and TTS not supported on this device.',
    listeningTtsFallback: 'Using text-to-speech — audio file unavailable for this exercise.',
    listeningClassroom: 'Listening class',
    listeningTeacherNote: 'Task',
    listeningStepListen: 'Listen',
    listeningStepAnswer: 'Answer',
    listeningStepCheck: 'Check',
    listeningPlayedTimes: 'Played {n}×',
    listeningSeek: 'Audio position',
    listeningSpeed: 'Playback speed',
    listeningRestart: 'Play from the start',
    listeningBackSeconds: 'Back {n} seconds',
    listeningForwardSeconds: 'Forward {n} seconds',
    listeningShortcutHint: 'Shortcuts: Space to play or pause, ← → to skip 5 seconds.',
    listeningTranscriptHint: 'Listen a few times first, then read along to check what you heard.',
    listeningAnswerSheet: 'Answer sheet',
    listeningQuestionNumber: 'Question {n}',
    listeningUnanswered: 'not answered',
    listeningReplay: 'Replay',
    audioPlayer: 'Audio Player',
    clickToPlayOrPause: 'Click to play or pause',
    showTranscript: 'Show Transcript',
    hideTranscript: 'Hide Transcript',
    transcript: 'Transcript',
    questions: 'Questions',
    submitAnswers: 'Submit Answers',
    newExercise: 'New Exercise',
    keepPracticingListening: 'Keep practicing to improve your listening skills',
    audioNotSupported: 'Your browser does not support audio playback',
    
    // Speaking
    speakingPractice: 'Speaking Practice',
    improveYourSpeakingSkills: 'Improve your speaking skills',
    duration: 'Duration',
    tips: 'Tips',
    recordYourResponse: 'Record your response',
    recording: 'Recording',
    recordingComplete: 'Recording Complete',
    clickPlayToListenOrRecordAgain: 'Click play to listen or record again',
    clickToStartRecording: 'Click the microphone to start recording',
    submitRecording: 'Submit Recording',
    newPrompt: 'New Prompt',
    keepPracticingSpeaking: 'Keep practicing to improve your speaking skills',
    speakingChooseLevel: 'Choose your level',
    speakingChooseLevelHint: 'Start with Level 1 and work your way up',
    speakingPickShelf: 'Filter by level, then pick a conversation by title',
    speakingChooseLesson: 'Choose a conversation',
    speakingLesson: 'conversation',
    speakingLessons: 'conversations',
    speakingNoLessons: 'No speaking lessons found for this level.',
    speakingBackToLessons: 'Back to lessons',
    speakingBackToLevels: 'Back to levels',
    speakingAiChat: 'AI chat',
    speakingLines: 'lines',
    speakingVocabWords: 'words',
    elementary: 'Elementary',
    speakingSessionBrief: 'Session brief',
    speakingPracticeWithAi: 'Practice with AI',
    speakingPracticeWithAiHint: 'Speak or type your replies — the AI partner will respond and coach you',
    speakingStartAiConversation: 'Start AI conversation',
    speakingStarting: 'Starting…',
    speakingYou: 'You',
    speakingAiPartner: 'AI partner',
    speakingAiThinking: 'AI is thinking…',
    speakingTypeReply: 'Type your reply…',
    speakingSend: 'Send',
    speakingSpeak: 'Speak',
    speakingListening: 'Listening…',
    speakingStartRecording: 'Start recording',
    speakingStopRecording: 'Stop recording',
    speakingRecordIdleHint: 'Tap Start recording, say your reply out loud, then tap Stop recording.',
    speakingRecordingHint: 'Recording. Tap Stop recording when you finish speaking.',
    speakingRecordingStopped: 'Recording stopped. Check the words below, then send.',
    speakingRecordingEmpty: 'Recording stopped. No words were caught. Try again, or type your reply.',
    speakingListeningForWords: 'Listening for your voice…',
    speakingMicBlocked: 'Microphone access is blocked. Allow the microphone, or type your reply.',
    speakingCouldNotHear: 'Could not hear you. Try again or type your reply.',
    speakingRecordUnsupported: 'This browser cannot record speech. Type your reply instead.',
    speakingOrType: 'Or type your reply',
    speakingEndSession: 'End session & get feedback',
    speakingSaving: 'Saving…',
    speakingNewSession: 'New session',
    speakingReferenceScript: 'Reference script',
    speakingKeyVocabulary: 'Key vocabulary',
    speakingSessionComplete: 'Session complete',
    speakingSavedToAccount: 'Saved to your account',
    speakingStrengths: 'Strengths',
    speakingHowToImprove: 'How to improve',
    speakingHowToPractice: 'How to practice',
    speakingHowToPracticeStep1: 'Choose a conversation from your current level.',
    speakingHowToPracticeStep2: 'Start an AI conversation and reply by speaking or typing.',
    speakingHowToPracticeStep3: 'Listen to the AI partner and use the reference script if you need help.',
    speakingHowToPracticeStep4: 'End the session to get feedback and save your progress.',
    speakingNoConversations: 'No conversations at this level yet.',
    speakingStartPractice: 'Start practice',
    speakingLoadingConversation: 'Loading conversation…',
    speakingPerson: 'Person',
    speakingConversations: 'conversations',
    speakingConversation: 'conversation',
    speakingLevel: 'Level',
    speakingMin: 'min',
    microphonePermissionError: 'Microphone permission denied. Please allow microphone access to record audio.',
    submitting: 'Submitting',
    startLesson: 'Start Lesson',
    viewProgress: 'View Progress',
    
    // Reading
    readingExercise: 'Reading Exercise',
    readPassage: 'Read the following passage and answer the questions',
    readingChooseLevel: 'Choose Your Level',
    readingPickShelf: 'Filter lessons by level, then pick a title to start',
    readingAllLevels: 'All Levels',
    readingChooseLesson: 'Choose a lesson',
    readingLesson: 'lesson',
    readingLessons: 'lessons',
    readingStartLesson: 'Start lesson',
    readingNoLessons: 'No lessons found for this level.',
    readingBackToLessons: 'Back to lessons',
    readingMinRead: 'min read',
    readingQuestionsCount: 'questions',
    readingStartTimer: 'Start Reading Timer',
    readingPauseTimer: 'Pause Timer',
    readingShowHistory: 'Show History',
    readingHide: 'Hide',
    readingPassageTitle: 'Reading Passage',
    readingOpenBook: 'Open book',
    readingNoPassage: 'No passage on this page',
    readingChooseAnother: 'Load another exercise to continue',
    readingLoadNewExercise: 'Load New Exercise',
    readingSelected: 'Selected:',
    readingDismiss: 'Dismiss',
    readingComprehensionQuestions: 'Comprehension Questions',
    readingAnswerFromPassage: 'Answer from the passage — scan carefully for clues',
    readingAnswered: 'answered',
    readingMultipleChoice: 'Multiple-choice',
    readingMultipleChoiceDesc: 'Choose the best option based on the passage.',
    readingTrueFalse: 'True / False',
    readingTrueFalseDesc: 'Decide if each statement matches the passage.',
    readingFillBlank: 'Fill in the blanks',
    readingFillBlankDesc: 'Type your answer using information from the text.',
    readingQuestionSingular: 'question',
    readingQuestionsPlural: 'questions',
    readingWriteAnswer: 'Write your answer...',
    readingChapterComplete: 'Chapter Complete',
    readingScoreExcellent: 'Excellent work — ready for the next level!',
    readingScoreGood: 'Good progress. Keep reading!',
    readingScoreKeep: 'Keep practicing — every page counts.',
    readingBestScore: 'Best score:',
    readingErrorLoading: 'Error Loading Exercise',
    readingAttempt: 'Attempt',
    readingResubmitTitle: 'Resubmit this exercise?',
    readingResubmitTimes: 'times',
    readingResubmitTime: 'time',
    readingResubmitAlready: "You've already attempted this exercise",
    readingResubmitBest: 'Your best score is',
    readingResubmitNote: 'Submitting again will create a new attempt and update your progress.',
    readingGoBack: 'Go Back',
    readingSubmitAnyway: 'Submit Anyway',
    readingTrue: 'True',
    readingFalse: 'False',
    question: 'Question',
    of: 'of',
    correctAnswer: 'Correct!',
    incorrectAnswer: 'Incorrect',
    yourScore: 'Your Score',
    score: 'Score',
    greatWork: 'Great Work!',
    
    // Writing
    writingPrompt: 'Writing Prompt',
    writingChooseLevel: 'Choose Your Level',
    writingPickShelf: 'Filter prompts by level, then pick a title to start',
    writingChooseLesson: 'Choose a lesson',
    writingLesson: 'lesson',
    writingLessons: 'lessons',
    writingStartLesson: 'Start writing',
    writingNoLessons: 'No lessons found for this level.',
    writingBackToLessons: 'Back to lessons',
    writeResponse: 'Write your response below',
    wordCount: 'Word Count',
    minWords: 'Minimum Words',
    submitWriting: 'Submit Writing',
    allLevels: 'All Levels',
    yourResponse: 'Your Response',
    writeResponsePlaceholder: 'Write your response here...',
    wordsLabel: 'Words',
    sentencesLabel: 'Sentences',
    minWordsSuggested: 'Min. {count} words suggested',
    showExampleAnswer: 'Show example answer',
    previousAttempts: 'Previous attempts',
    bestScoreLabel: 'Best score',
    submitForGrade: 'Submit for grade & feedback',
    gradingWriting: 'Grading your writing…',
    aiReadingEssay: 'AI is reading your essay — this may take up to a minute…',
    writingSaved: 'Your writing has been saved to your account',
    attemptLabel: 'Attempt',
    yourGrade: 'Your grade',
    excellentWriting: 'Excellent writing — ready for the next lesson!',
    goodWorkKeepPracticing: 'Good work. Keep practicing!',
    keepWritingDraft: 'Keep writing — every draft helps you improve.',
    scoreFromAi: 'Score from AI teacher',
    scoreFromLength: 'Score based on length & structure',
    teacherFeedback: 'Teacher feedback',
    gradedByAi: 'Graded by AI',
    gradedAutomatically: 'Graded automatically',
    rubricTask: 'Task',
    rubricGrammar: 'Grammar',
    rubricVocabulary: 'Vocabulary',
    rubricOrganization: 'Organization',
    strengths: 'Strengths',
    howToImprove: 'How to improve',
    grammarTips: 'Grammar tips',
    writingResubmitTitle: 'Resubmit this writing?',
    
    // Profile
    bio: 'Bio',
    nativeLanguage: 'Native Language',
    englishLevel: 'English Level',
    learningGoal: 'Learning Goal',
    profilePicture: 'Profile Picture',
    chooseAvatar: 'Choose avatar',
    removePhoto: 'Remove photo',
    personalInfoSaved: 'Personal information saved successfully!',
    learningProfileSaved: 'Learning profile saved successfully!',
    oopsSomethingWentWrong: 'Oops! Something went wrong',
    loadingYourProfile: 'Loading your profile...',
    thisWontTakeLong: 'This won\'t take long',
    personalInformation: 'Personal Information',
    enterFirstName: 'Enter first name',
    enterLastName: 'Enter last name',
    saving: 'Saving...',
    savePersonalInformation: 'Save Personal Information',
    learningProfile: 'Learning Profile',
    tellUsAboutJourney: 'Tell us about your learning journey...',
    selectLevel: 'Select level',
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
    egSpanish: 'e.g., Spanish',
    egPassTOEFL: 'e.g., Pass TOEFL exam',
    dailyGoal: 'Daily Goal',
    saveLearningProfile: 'Save Learning Profile',
    learningProgress: 'Learning Progress',
    setYourLearningObjective: 'Set your learning objective',
    dailyMinutes: 'Daily Minutes',
    level: 'Level',
    ofDailyGoal: '% of daily goal',
    welcomeToJourney: 'Welcome to your language learning journey! Set your goals and track your progress.',
    memberSince: 'Member since',
    accountStatus: 'Account status',
    accountStatusActive: 'Active',
    accountStatusInactive: 'Inactive',
    userId: 'User ID',
    profileId: 'Profile ID',
    adminBadge: 'Admin',
    profileAccountReadOnlyNote: 'Email and username are from your account and cannot be changed on this screen.',
    nationality: 'Nationality',
    currentLocation: 'Current location',
    selectCountry: 'Select a country',
    searchCountry: 'Search countries',
    clearCountry: 'Clear selection',
    profilePersonalInfoHint: 'Update your name, nationality, and current location.',
    profileLearningProgressHint: 'Your goals and daily study target.',
    profileLearningProfileHint: 'Customize how you learn on the platform.',
    
    // Activities
    yourLanguageJourney: 'Your Language Journey',
    trackProgressCelebrate: 'Track your progress, celebrate achievements, and stay motivated on your path to fluency',
    totalActivities: 'Total Activities',
    dayStreak: 'Day Streak',
    master: 'Master',
    good: 'Good',
    gettingStarted: 'Getting Started',
    completed: 'Completed',
    lessonsStarted: 'lessons started',
    profileUpdates: 'Profile Updates',
    totalLogins: 'total logins',
    recentActivities: 'Recent Activities',
    latestLearningMilestones: 'Your latest learning milestones and achievements',
    allActivities: 'All Activities',
    completedLessons: 'Completed Lessons',
    startedLessons: 'Started Lessons',
    profileUpdatesFilter: 'Profile Updates',
    logins: 'Logins',
    noActivitiesFound: 'No activities found',
    tryChangingFilter: 'Try changing the filter or start a new lesson!',
    activityId: 'Activity ID:',
    type: 'Type',
    timestamp: 'Timestamp',
    readyForMore: 'Ready for more?',
    continueLearningJourney: 'Continue your learning journey with new activities and challenges',
    exploreMoreActivities: 'Explore More Activities',
    pageLabel: 'Page',
    previous: 'Previous',
    next: 'Next',
    activityTypeLessonStarted: 'Lesson started',
    activityTypeLessonCompleted: 'Lesson completed',
    activityTypeProfileUpdated: 'Profile updated',
    activityTypeLogin: 'Sign in',
    activityTypeWritingPractice: 'Writing practice',
    activityTypeReadingPractice: 'Reading practice',
    activityTypeSpeakingPractice: 'Speaking practice',
    activityTypeListeningPractice: 'Listening practice',
    activityTypeVocabularySaved: 'Word saved',
    activityTypeVocabularyReviewed: 'Word reviewed',
    activityTypeVocabularyQuiz: 'Vocabulary quiz',
    vocabularyFilter: 'Vocabulary',
    activityTypeDefault: 'Activity',
    activityDetails: 'Details',
    expandActivity: 'Expand',
    collapseActivity: 'Hide',
    
    // Settings
    settingsSubtitle: 'Customize your learning experience',
    focusArea: 'Focus area',
    focusAreaDescription: 'Choose what the dashboard should prioritize for practice.',
    focusAreaBalanced: 'Balanced',
    focusAreaBalancedHint: 'Auto-suggest your weakest skill',
    focusAreaReadingHint: 'Prioritize reading on the dashboard',
    focusAreaWritingHint: 'Prioritize writing on the dashboard',
    focusAreaListeningHint: 'Prioritize listening on the dashboard',
    focusAreaSpeakingHint: 'Prioritize speaking on the dashboard',
    focusAreaGrammarHint: 'Prioritize grammar on the dashboard',
    focusAreaSaved: 'Learning preferences saved',
    saveDailyGoal: 'Save daily goal',
    dailyGoalSettingsHint: 'Shown on your dashboard as today’s study target.',
    viewDashboard: 'Open dashboard',
    loading: 'Loading…',
    preferences: 'Preferences',
    currentTheme: 'Current theme',
    currentLanguage: 'Current language',
    fontSize: 'Font size',
    fontSizeDescription: 'Choose a comfortable reading size for the whole app.',
    fontSizeSmall: 'Small',
    fontSizeMedium: 'Medium',
    fontSizeLarge: 'Large',
    currentFontSize: 'Current font size',
    preview: 'Preview',
    previewDescription: 'This preview shows how your settings will appear throughout the app.',
    
    // Footer
    home: 'Home',
    navigation: 'Navigation',
    support: 'Support',
    contactUs: 'Contact Us',
    allRightsReserved: 'All rights reserved.',
    termsAndConditions: 'Terms',
    footerDescription: 'Master English with interactive exercises and personalized learning paths.',
    
    // Home Page
    startLearningFree: 'Start Learning Free',
    goToDashboard: 'Go to Dashboard',
    homeBadge: 'Trusted by learners worldwide',
    homeTitle1: 'English practice that stays with you.',
    homeTitle2: 'One Skill at a Time',
    homeTitle3: 'Real Progress. Real Results.',
    homeHeroLead: 'Now practicing',
    homeHeroLabelReading: 'Reading — see meaning in every line',
    homeHeroLabelWriting: 'Writing — shape thoughts into clear words',
    homeHeroLabelListening: 'Listening — catch every sound and cue',
    homeHeroLabelSpeaking: 'Speaking — find your voice with ease',
    homeBillboardLead: 'The place to',
    homeHeroKicker: 'Four skills, one journey',
    homeHeroLine: 'The place',
    homeHeroTo: 'to',
    homeBillboardReading: 'read',
    homeBillboardWriting: 'write',
    homeBillboardListening: 'listen',
    homeBillboardSpeaking: 'speak',
    homeSubtitle: 'Your Journey to Better English Starts Here',
    homeHeroStory: 'Explore a World of Stories While Mastering English. Discover Stories. Improve Your English. Read More. Learn More. Speak with Confidence.',
    homeHeroStatLessons: 'Lessons',
    homeHeroStatSkills: 'Core skills',
    homeHeroStatAnytime: 'Anytime Learning',
    homeHeroStatLevels: 'Learning Levels',
    homeHeroStatLanguages: 'Languages',
    homeHeroStatFree: 'to Start',
    homeHeroStatVocab: 'Vocabulary Words',
    homeHeroStatExercises: 'Interactive Exercises',
    homeHeroStatGrammar: 'Grammar Activities',
    homeHeroStatTopics: 'Learning Topics',
    homeHeroStatPersonal: 'Personalized Learning',
    homeHeroStatDaily: 'Daily Practice',
    homeHeroStatPlatform: 'All-in-One Learning',
    homeTrust1: 'Free Forever',
    homeTrust2: 'No Credit Card',
    homeTrust3: 'Start in 30 Seconds',
    homeFeaturesTitle: 'Master All Four Core Skills',
    homeFeaturesSubtitle: 'Comprehensive language learning through targeted skill development',
    homeEverythingTitle: 'Everything you need.',
    homeEverythingSubtitle: 'Nothing extra. Just the practice that moves you forward.',
    homeMarqueeExercises: '50+ exercises',
    homeMarqueeSkills: '4 core skills',
    homeMarqueeLanguages: '3 languages',
    homeMarqueeFree: '100% free',
    homePickSkill: 'Choose a skill',
    homeReadingDesc: 'Improve comprehension, expand vocabulary, and analyze texts with engaging reading exercises at your level.',
    homeWritingDesc: 'Express ideas clearly, master grammar, and develop your writing style through structured practice.',
    homeListeningDesc: 'Enhance pronunciation, understand accents, and improve audio comprehension with real-world content.',
    homeSpeakingDesc: 'Build confidence, practice conversation, and achieve fluency through interactive speaking exercises.',
    homeHowItWorksTitle: 'How It Works',
    homeHowItWorksSubtitle: 'Get started in three simple steps',
    homeStep1Title: 'Create Your Account',
    homeStep1Desc: 'Sign up in seconds with just your email. No credit card required. Start learning immediately.',
    homeStep2Title: 'Choose Your Skill',
    homeStep2Desc: 'Select Reading, Writing, Listening, or Speaking. Each skill has exercises tailored to your level.',
    homeStep3Title: 'Start Practicing',
    homeStep3Desc: 'Begin with exercises designed for your proficiency level. Track progress and improve daily.',
    homeWhyChooseTitle: 'Why Choose Us',
    homeWhyChooseSubtitle: 'What makes our platform different',
    homeBenefit1Title: 'Personalized Learning',
    homeBenefit1Desc: 'Exercises adapt to your skill level, ensuring optimal challenge and growth at every step.',
    homeBenefit2Title: 'Progress Tracking',
    homeBenefit2Desc: 'Monitor your improvement with detailed analytics, streaks, and comprehensive progress reports.',
    homeBenefit3Title: 'Multilingual Support',
    homeBenefit3Desc: 'Full interface support in English, Burmese, and Chinese, making learning accessible and comfortable.',
    homeBenefit4Title: 'Instant Feedback',
    homeBenefit4Desc: 'Get immediate results and explanations for every exercise, helping you learn from mistakes.',
    homeBenefit5Title: 'Structured Curriculum',
    homeBenefit5Desc: 'Follow a clear learning path from beginner to advanced, with exercises that build on each other.',
    homeBenefit6Title: 'Flexible Practice',
    homeBenefit6Desc: 'Learn at your own pace, anytime, anywhere. Practice when it suits your schedule.',
    homeStat1: 'Interactive Exercises',
    homeStat2: 'Core Skills',
    homeStat3: 'Languages',
    homeStat4: 'Free Access',
    homeCTATitle: 'Ready to Begin Your Journey?',
    homeCTASubtitle: 'Join thousands of learners improving their English every day. Start free, no credit card required.',
    homeOrbitLabel: 'Your learning orbit',
    homeOrbitCenter: '4 skills · 1 journey',
    homePreviewLabel: 'Live preview',
    homePreviewReading1: 'The expedition crossed the valley before sunrise.',
    homePreviewReading2a: 'They reached the ',
    homePreviewReadingHighlight: 'ancient ridge',
    homePreviewReading2b: ' at dawn.',
    homePreviewWritingDraft: 'I have working in tech since three years.',
    homePreviewWritingFix: 'I have been working in tech for three years.',
    homePreviewListening: 'Listen and complete the sentence',
    homePreviewSpeakingQ: 'Introduce yourself in 30 seconds.',
    homePreviewSpeakingA: '"Hi, I\'m Maya — I design learning tools."',
    homePreviewMetric: 'Session accuracy',
    homeExploreSkills: 'Practice each skill',
    homePracticeSkill: 'Practice this skill',
    
    // Common additional
    nextLanguage: 'Next Language',
    errorOccurred: 'An unexpected error occurred. Please try again.',
    reloadPage: 'Reload Page',
    pauseAudio: 'Pause audio',
    playAudio: 'Play audio',
    feedback: 'Feedback',
    submitSuggestion: 'Suggestion',
    manageFeedback: 'Feedback',
    haveSuggestion: 'Have a suggestion?',
  },
  
  my: {
    // Navigation
    dashboard: 'ပင်မစာမျက်နှာ',
    reading: 'အဖတ်',
    writing: 'အရေး',
    listening: 'နားထောင်ခြင်း',
    speaking: 'အပြော',
    vocabulary: 'စကားလုံး',
    grammar: 'သဒ္ဒါ',
    grammarPractice: 'လေ့ကျင့်ရန်',
    grammarPageLead: 'ပုံစံတစ်ခုကို သင်ယူပါ၊ ထို့နောက် ရောနှောလေ့ကျင့်ပါ။ မှန်ကန်သော ကာလကို ရွေးနိုင်မှ ကျွမ်းကျင်သည်။',
    grammarComingSoon: 'မကြာမီ',
    grammarItems: 'မေးခွန်း',
    grammarCheck: 'အဖြေစစ်ရန်',
    grammarScore: 'ရမှတ်',
    grammarTryAgain: 'ထပ်ကြိုးစားရန်',
    grammarBackToTopics: 'ခေါင်းစဉ်အားလုံး',
    grammarHint: 'အရိပ်အမြွက်',
    grammarExplanation: 'အကြောင်း',
    grammarPerson: 'ကတ္တား',
    grammarPositive: 'အပြုသဘော',
    grammarNegative: 'အငြင်း',
    grammarQuestion: 'မေးခွန်း',
    grammarGroup_present: 'ပစ္စုပ္ပန်',
    grammarGroup_past: 'အတိတ်',
    grammarGroup_future: 'အနာဂတ်',
    grammarGroup_articles: 'နာမ်ရှေ့စာလုံး',
    grammarGroup_agreement: 'ကတ္တားနှင့်ကြိယာ',
    grammarGroup_prepositions: 'ဝိဘတ်',
    grammarGroup_comparatives: 'နှိုင်းယှဉ်ပုံ',
    grammarGroup_conditionals: 'အကယ်၍ဝါကျ',
    grammarGroup_modals: 'ကြိယာထောက်',
    grammarGroup_reported: 'သွယ်ဝိုက်စကား',
    grammarGroup_passives: 'အကံပြုဝါကျ',
    grammarGroup_mastery: 'ရောနှောလေ့ကျင့်',
    grammarMastered: 'ကျွမ်းကျင်ပြီး',
    grammarCompleted: 'ပြီးပြီ',
    grammarBest: 'အကောင်းဆုံး',
    grammarAnswer: 'အဖြေ',
    grammarRetryMissed: 'မှားသည်များကို လေ့ကျင့်',
    grammarRetryMissedHint: 'မှားခဲ့သော မေးခွန်းများသာ။ မှန်ပြီးသားကို သိမ်းထားသည်။',
    grammarNextTopic: 'နောက်တစ်ခု',
    grammarRecommended: 'ဆက်လေ့ကျင့်ရန်',
    grammarMasteryCount: '{total} ခုအနက် {n} ခု ပြီး',
    activities: 'လုပ်ဆောင်ချက်',
    profile: 'မိမိ',
    settings: 'ဆက်တင်',
    login: 'ဝင်မည်',
    signup: 'စာရင်းသွင်း',
    logout: 'ထွက်မည်',
    adminPanel: 'အက်မင်',
    
    // Common
    welcome: 'ကြိုဆိုပါတယ်',
    tryAgain: 'ထပ်ကြိုးစားရန်',
    
    // Dashboard
    welcomeBack: 'ပြန်လည်ကြိုဆိုပါတယ်',
    readyToContinue: 'သင်ယူမှုခရီးကို စတင်ရန် အဆင်သင့်ဖြစ်ပြီလား？',
    lessonsCompleted: 'ပြီးဆုံးသောသင်ခန်းစာများ',
    timeSpent: 'သုံးစွဲသောအချိန်',
    averageScore: 'ပျမ်းမျှအမှတ်',
    learningStreak: 'သင်ယူမှုအစဉ်လိုက်',
    skillsProgress: 'ကျွမ်းကျင်မှုတိုးတက်မှု',
    recentActivity: 'လတ်တလောလုပ်ဆောင်ချက်များ',
    noRecentActivity: 'လတ်တလောလုပ်ဆောင်ချက်မရှိသေးပါ',
    minutes: 'မိနစ်',
    days: 'ရက်',
    loadingCosmicJourney: 'သင့်သင်ယူမှုခရီးကို ဖွင့်နေသည်...',
    somethingWentWrong: 'တစ်ခုခု မှားယွင်းနေပါတယ်',
    yourLearningDashboard: 'သင့်သင်ယူမှု ဒက်ရှ်ဘုတ်',
    continueCosmicJourney: 'သင့်သင်ယူမှုခရီးကို ဆက်လက်လုပ်ဆောင်ပြီး အသိပညာ၏ နယ်ပယ်အသစ်များကို စူးစမ်းပါ',
    trackYourMastery: 'ကျွမ်းကျင်မှုများကို ခြေရာခံပါ',
    noSkillsDataAvailable: 'ကျွမ်းကျင်မှု ဒေတာမရှိသေးပါ',
    dashboardSubtitle: 'သင့်တိုးတက်မှုကို တစ်ချက်ကြည့်ပါ — ဆက်လက်လေ့လာပါ။',
    dashboardWeeklyActivity: 'ဤအပတ်',
    dashboardWeeklyActivityHint: 'နေ့စဉ် ပြီးမြောက်သော သင်ခန်းစာများ',
    dashboardQuickPractice: 'အမြန်လေ့ကျင့်ခန်း',
    dashboardQuickPracticeHint: 'ကျွမ်းကျင်မှုတစ်ခုသို့ ချက်ချင်းသွားရန်',
    dashboardFocusArea: 'အာရုံစိုက်ရမည့်နေရာ',
    dashboardCustomize: 'စိတ်ကြိုက်',
    dashboardDailyGoal: 'ယနေ့ပန်းတိုင်',
    dashboardDailyGoalMet: 'နေ့စဉ်ပန်းတိုင် ပြည့်မီပြီ — ကောင်းပါသည်!',
    dashboardDailyGoalHint: 'ယနေ့ပန်းတိုင်၏ {pct}% ပြီးမြောက်',
    dashboardYourFocus: 'သင့်အာရုံစိုက်မှု',
    dashboardFocusFromSettings: 'ဆက်တင်မှ သင့်ရွေးချယ်မှု',
    dashboardFocusAutoWeakest: 'အနည်းဆုံးကျွမ်းကျင်မှုမှ အကြံပြုသည်',
    dashboardPickFocus: 'ဆက်တင်တွင် အာရုံစိုက်မှု ရွေးပါ',
    dashboardFocusTag: 'အာရုံ',
    dashboardStartedShort: 'စတင်',
    dashboardRecentActivity: 'လတ်တလော လုပ်ဆောင်ချက်',
    dashboardRecentActivityHint: 'အကောင့်မှ နောက်ဆုံး သင်ယူမှု လုပ်ဆောင်ချက်များ',
    dashboardNoRecentActivity: 'လတ်တလော လုပ်ဆောင်ချက် မရှိသေးပါ — သင်ခန်းစာ စတင်ပါ။',
    dashboardStrongestSkill: 'အင်အားအရှိဆုံး ကျွမ်းကျင်မှု',
    dashboardViewActivities: 'လုပ်ဆောင်ချက်အားလုံးကြည့်ရန်',
    dashboardLessonsThisWeek: 'ဤအပတ်သင်ခန်းစာများ',
    dashboardActiveDays: 'လှုပ်ရှားသော နေ့များ',
    dashboardNoWeeklyData: 'ဤအပတ်တွင် ပြီးမြောက်သော သင်ခန်းစာ မရှိသေးပါ။ ယနေ့ လေ့ကျင့်ခန်းတစ်ခု စတင်ပါ။',
    dashboardPracticeNow: 'ယခု လေ့ကျင့်ရန်',
    dashboardOverallProgress: 'စုစုပေါင်း တိုးတက်မှု',
    dashboardStreakActive: 'သင့်ဆက်တိုက်လေ့လာမှုကို ဆက်ထိန်းပါ',
    dashboardStreakStart: 'သင်ခန်းစာတစ်ခု စတင်ပါ သို့မဟုတ် စကားလုံးများကို ပြန်လည်သုံးသပ်ပါ',
    dashboardVocabLearned: 'လေ့လာပြီး စကားလုံးများ',
    dashboardVocabOfSaved: 'သိမ်းထားသည့် {total} အနက်',
    dashboardVocabInProgress: '{n} လုပ်ဆောင်နေဆဲ',
    dashboardVocabNoneSaved: 'ပထမဆုံး စကားလုံးကို သိမ်းပါ',
    
    // Settings
    appearance: 'အပြင်အဆင်',
    language: 'ဘာသာစကား',
    theme: 'အပြင်အဆင်',
    lightMode: 'အလင်းပုံစံ',
    darkMode: 'အမှောင်ပုံစံ',
    systemMode: 'လက်ရှိစနစ်',
    selectLanguage: 'ဘာသာစကားရွေးချယ်ရန်',
    english: 'English',
    burmese: 'မြန်မာ',
    chinese: '中文',
    
    // Auth
    email: 'အီးမေးလ်',
    password: 'စကားဝှက်',
    showPassword: 'စကားဝှက် ပြရန်',
    hidePassword: 'စကားဝှက် ဖျောက်ရန်',
    confirmPassword: 'စကားဝှက်အတည်ပြုရန်',
    firstName: 'အမည်',
    lastName: 'အမျိုးအမည်',
    username: 'အသုံးပြုသူအမည်',
    signIn: 'ဝင်ရောက်ရန်',
    signUp: 'စာရင်းသွင်းရန်',
    createAccount: 'အကောင့်ဖန်တီးရန်',
    creatingAccount: 'အကောင့်ဖန်တီးနေသည်',
    signingIn: 'ဝင်ရောက်နေသည်',
    joinUsToGetStarted: 'စတင်ရန် ကျွန်ုပ်တို့နှင့်ပူးပေါင်းပါ',
    alreadyHaveAccount: 'အကောင့်ရှိပြီးသားလား?',
    dontHaveAccount: 'အကောင့်မရှိသေးဘူးလား?',
    forgotPassword: 'စကားဝှက် မေ့နေပါသလား?',
    forgotPasswordLead: 'သင့်အကောင့်အီးမေးလ်ကို ရိုက်ထည့်ပါ။ စာရင်းရှိလျှင် ပြန်သတ်မှတ်ရန် လင့်ခ် ပို့ပါမည်။',
    forgotPasswordSubmit: 'ပြန်သတ်မှတ်ရန် လင့်ခ် ပို့ရန်',
    forgotPasswordSent: 'ထိုအီးမေးလ် စာရင်းရှိလျှင် ပြန်သတ်မှတ်ရန် လင့်ခ် မကြာမီ ရရှိပါမည်။',
    sendingReset: 'ပို့နေသည်',
    backToSignIn: 'ဝင်ရောက်ရန် ပြန်သွားရန်',
    resetPassword: 'စကားဝှက် ပြန်သတ်မှတ်ရန်',
    resetPasswordLead: 'သင့်အကောင့်အတွက် စကားဝှက်အသစ် ရွေးပါ။',
    resetPasswordSubmit: 'စကားဝှက် အပ်ဒိတ်လုပ်ရန်',
    resetPasswordSuccess: 'စကားဝှက် ပြောင်းပြီးပါပြီ။ ယခု ဝင်ရောက်နိုင်ပါသည်။',
    resetPasswordInvalid: 'ဤလင့်ခ် မမှန်ကန်ပါ သို့မဟုတ် သက်တမ်းကုန်ပါပြီ။ အသစ်တောင်းပါ။',
    passwordsDoNotMatch: 'စကားဝှက်နှစ်ခု မတူပါ။',
    newPassword: 'စကားဝှက်အသစ်',
    confirmNewPassword: 'စကားဝှက်အသစ် အတည်ပြုရန်',
    authLibraryPortal: 'စာကြည့်တိုက် ဝင်ပေါက်',
    authNewRegistration: 'စာရင်းသွင်းမှု အသစ်',
    authWelcomeBack: 'ပြန်လည်ကြိုဆိုပါတယ်',
    authLoginTagline: 'အေးချမ်းပြီး အာရုံစိုက်နိုင်သော နေရာတွင် အင်္ဂလိပ်စာ ခရီးကို ဆက်လက်ပါ။',
    authLoginFormHint: 'သင့်အရှိန်အတိုင်း ဆက်လက်သင်ယူရန် ဝင်ရောက်ပါ။',
    authJoinHeadline: 'သင့်ခရီးကို စတင်ပါ',
    authSignupTagline: 'စာဖတ်၊ စာရေး၊ နားထောင်၊ စကားပြော တိုးတက်ရန် နွေးထွေးသော နေရာ။',
    authSignupFormHint: 'အခမဲ့ အကောင့်ဖန်တီးပါ — ခဏသာ ကြာပါသည်။',
    dismiss: 'ပယ်ဖျက်ရန်',
    
    // Skills
    selectSkill: 'ကျွမ်းကျင်မှုတစ်ခုကို ရွေးချယ်ရန်',
    lessons: 'သင်ခန်းစာများ',
    difficulty: 'အခက်အခဲ',
    
    // Listening
    listeningPractice: 'နားထောင် လေ့ကျင့်ခန်း',
    improveYourListeningSkills: 'သင့်နားထောင် စွမ်းရည်ကို မြှင့်တင်လိုက်ပါ',
    listenToTheAudioAndAnswerQuestions: 'အသံဖိုင်ကို နားထောင်ပြီး အောက်ပါ မေးခွန်းများကို ဖြေကြားပါ',
    listeningChooseLevel: 'အဆင့် ရွေးချယ်ပါ',
    listeningPickShelf: 'အဆင့်အလိုက် စစ်ထုတ်ပြီး အသံခေါင်းစဉ်ဖြင့် သင်ခန်းစာ ရွေးပါ',
    listeningChooseLesson: 'အသံ သင်ခန်းစာ ရွေးပါ',
    listeningLesson: 'သင်ခန်းစာ',
    listeningLessons: 'သင်ခန်းစာများ',
    listeningStartLesson: 'နားထောင် စတင်ရန်',
    listeningNoLessons: 'ဤအဆင့်အတွက် နားထောင် သင်ခန်းစာ မတွေ့ပါ။',
    listeningBackToLessons: 'သင်ခန်းစာများသို့ ပြန်ရန်',
    listeningHasAudio: 'အသံ',
    listeningVoiceOnly: 'အသံပြော',
    listeningMin: 'မိနစ်',
    listeningQuestionsCount: 'မေးခွန်းများ',
    listeningBooth: 'နားထောင် ခန်း',
    listeningNowPlaying: 'ဖွင့်နေသည်…',
    listeningPressPlay: 'အဆင်သင့်ဖြစ်လျှင် ဖွင့်ရန် နှိပ်ပါ',
    listeningAnswerHint: 'ဂရုတစိုက် နားထောင်ပြီး အကောင်းဆုံး အဖြေကို ရွေးပါ',
    listeningAnswered: 'ဖြေပြီး',
    listeningAudioUnavailable: 'အသံ မရနိုင်ဘဲ ဤစက်တွင် TTS မပံ့ပိုးပါ။',
    listeningTtsFallback: 'စာသားမှ အသံသို့ — ဤလေ့ကျင့်ခန်းအတွက် အသံဖိုင် မရနိုင်ပါ။',
    listeningClassroom: 'နားထောင် အတန်း',
    listeningTeacherNote: 'လုပ်ငန်း',
    listeningStepListen: 'နားထောင်',
    listeningStepAnswer: 'ဖြေဆို',
    listeningStepCheck: 'စစ်ဆေး',
    listeningPlayedTimes: '{n} ကြိမ် ဖွင့်ပြီး',
    listeningSeek: 'အသံ အနေအထား',
    listeningSpeed: 'ဖွင့်သည့် အရှိန်',
    listeningRestart: 'အစမှ ပြန်ဖွင့်ရန်',
    listeningBackSeconds: '{n} စက္ကန့် ပြန်ရန်',
    listeningForwardSeconds: '{n} စက္ကန့် ရှေ့သို့',
    listeningShortcutHint: 'ဖြတ်လမ်း — Space ဖွင့်/ရပ်၊ ← → ဖြင့် ၅ စက္ကန့် ခုန်ရန်။',
    listeningTranscriptHint: 'အရင် အနည်းငယ် နားထောင်ပြီးမှ စာသားဖြင့် ပြန်စစ်ပါ။',
    listeningAnswerSheet: 'အဖြေ စာရွက်',
    listeningQuestionNumber: 'မေးခွန်း {n}',
    listeningUnanswered: 'မဖြေရသေး',
    listeningReplay: 'ပြန်ဖွင့်',
    audioPlayer: 'အသံဖွင့်စက်',
    clickToPlayOrPause: 'ဖွင့်ရန် သို့မဟုတ် ရပ်ရန် နှိပ်ပါ',
    showTranscript: 'စာသားကို ပြပါ',
    hideTranscript: 'စာသားကို ဖျောက်ပါ',
    transcript: 'စာသား',
    questions: 'မေးခွန်းများ',
    submitAnswers: 'အဖြေများ ပို့ပါ',
    newExercise: 'လေ့ကျင့်ခန်း အသစ်',
    keepPracticingListening: 'နားထောင် စွမ်းရည်ကို မြှင့်တင်ရန် ဆက်လက်လေ့ကျင့်ပါ',
    audioNotSupported: 'သင့်ဘရောက်ဇာသည် အသံအစိတ်အပိုင်းကို မပံ့ပိုးပါ',
    score: 'အမှတ်',
    greatWork: 'ကောင်းမွန်ပါတယ်!',
    
    // Speaking
    speakingPractice: 'စကားပြော လေ့ကျင့်ခန်း',
    improveYourSpeakingSkills: 'သင့်စကားပြောခြင်း စွမ်းရည်ကို မြှင့်တင်ပါ',
    duration: 'ကြာချိန်',
    tips: 'အကြံပြုချက်များ',
    recordYourResponse: 'သင့်အဖြေကို မှတ်တမ်းတင်ပါ',
    recording: 'မှတ်တမ်းတင်နေသည်',
    recordingComplete: 'မှတ်တမ်းတင်ပြီးပါပြီ',
    clickPlayToListenOrRecordAgain: 'နားထောင်ရန် ဖွင့်ပါ သို့မဟုတ် ပြန်မှတ်တမ်းတင်ပါ',
    clickToStartRecording: 'မှတ်တမ်းတင်စရန် မိုက်ခရိုဖုန်းကို နှိပ်ပါ',
    submitRecording: 'မှတ်တမ်းတင်မှုကို ပို့ပါ',
    newPrompt: 'လမ်းညွှန်ချက် အသစ်',
    keepPracticingSpeaking: 'စကားပြော စွမ်းရည်တိုးတက်ရန် ဆက်လက်လေ့ကျင့်ပါ',
    speakingChooseLevel: 'သင့်အဆင့် ရွေးချယ်ပါ',
    speakingChooseLevelHint: 'အဆင့် ၁ မှ စတင်ပြီး ဖြည်းဖြည်း တက်လှမ်းပါ',
    speakingPickShelf: 'အဆင့်အလိုက် စစ်ထုတ်ပြီး ခေါင်းစဉ်ဖြင့် စကားပြော သင်ခန်းစာ ရွေးပါ',
    speakingChooseLesson: 'စကားပြော သင်ခန်းစာ ရွေးပါ',
    speakingLesson: 'စကားပြော',
    speakingLessons: 'စကားပြောများ',
    speakingNoLessons: 'ဤအဆင့်အတွက် စကားပြော သင်ခန်းစာ မတွေ့ပါ။',
    speakingBackToLessons: 'သင်ခန်းစာများသို့ ပြန်ရန်',
    speakingBackToLevels: 'အဆင့်များသို့ ပြန်သွားရန်',
    speakingAiChat: 'AI စကားပြော',
    speakingLines: 'စာကြောင်းများ',
    speakingVocabWords: 'စကားလုံးများ',
    elementary: 'အခြေခံ',
    speakingSessionBrief: 'သင်တန်းအကျဉ်းချုပ်',
    speakingPracticeWithAi: 'AI နှင့် လေ့ကျင့်ပါ',
    speakingPracticeWithAiHint: 'စကားပြောပါ သို့မဟုတ် ရိုက်ထည့်ပါ — AI မိတ်ဖက်က ပြန်ဖြေပြီး လမ်းညွှန်ပေးပါမည်',
    speakingStartAiConversation: 'AI စကားပြောစတင်ရန်',
    speakingStarting: 'စတင်နေသည်…',
    speakingYou: 'သင်',
    speakingAiPartner: 'AI မိတ်ဖက်',
    speakingAiThinking: 'AI စဉ်းစားနေသည်…',
    speakingTypeReply: 'သင့်အဖြေ ရိုက်ထည့်ပါ…',
    speakingSend: 'ပို့ရန်',
    speakingSpeak: 'စကားပြောရန်',
    speakingListening: 'နားထောင်နေသည်…',
    speakingStartRecording: 'မှတ်တမ်းတင် စတင်ရန်',
    speakingStopRecording: 'မှတ်တမ်းတင် ရပ်ရန်',
    speakingRecordIdleHint: 'မှတ်တမ်းတင် စတင်ရန် ကို နှိပ်ပါ၊ အဖြေကို ပြောပါ၊ ပြီးရင် မှတ်တမ်းတင် ရပ်ရန် ကို နှိပ်ပါ။',
    speakingRecordingHint: 'မှတ်တမ်းတင်နေသည်။ ပြောပြီးရင် မှတ်တမ်းတင် ရပ်ရန် ကို နှိပ်ပါ။',
    speakingRecordingStopped: 'မှတ်တမ်းတင် ရပ်ပြီးပါပြီ။ အောက်က စာလုံးများကို စစ်ဆေးပြီး ပို့ပါ။',
    speakingRecordingEmpty: 'မှတ်တမ်းတင် ရပ်ပြီးပါပြီ။ စကားသံ မမိပါ။ ထပ်ကြိုးစားပါ သို့မဟုတ် ရိုက်ထည့်ပါ။',
    speakingListeningForWords: 'သင့်အသံကို နားထောင်နေသည်…',
    speakingMicBlocked: 'မိုက်ခရိုဖုန်းကို ခွင့်မပြုထားပါ။ မိုက်ခရိုဖုန်းကို ဖွင့်ပါ သို့မဟုတ် ရိုက်ထည့်ပါ။',
    speakingCouldNotHear: 'အသံမကြားပါ။ ထပ်ကြိုးစားပါ သို့မဟုတ် ရိုက်ထည့်ပါ။',
    speakingRecordUnsupported: 'ဤဘရောက်ဇာသည် စကားသံ မှတ်တမ်းတင်ခြင်းကို မပံ့ပိုးပါ။ အဖြေကို ရိုက်ထည့်ပါ။',
    speakingOrType: 'သို့မဟုတ် အဖြေကို ရိုက်ထည့်ပါ',
    speakingEndSession: 'သင်တန်းအဆုံးသတ်ပြီး အကြံပြုချက်ရယူရန်',
    speakingSaving: 'သိမ်းဆည်းနေသည်…',
    speakingNewSession: 'သင်တန်းအသစ်',
    speakingReferenceScript: 'ကိုးကားဇာတ်ညွှန်း',
    speakingKeyVocabulary: 'အဓိက ဝေါဟာရ',
    speakingSessionComplete: 'သင်တန်းပြီးဆုံး',
    speakingSavedToAccount: 'သင့်အကောင့်သို့ သိမ်းဆည်းပြီး',
    speakingStrengths: 'အားသာချက်များ',
    speakingHowToImprove: 'တိုးတက်နည်းလမ်း',
    speakingHowToPractice: 'လေ့ကျင့်နည်း',
    speakingHowToPracticeStep1: 'လက်ရှိအဆင့်မှ စကားပြောခန်းတစ်ခု ရွေးပါ။',
    speakingHowToPracticeStep2: 'AI စကားပြောကို စတင်ပြီး စကားပြောခြင်း သို့မဟုတ် ရိုက်ထည့်ခြင်းဖြင့် ဖြေပါ။',
    speakingHowToPracticeStep3: 'AI မိတ်ဖက်ကို နားထောင်ပြီး လိုအပ်ပါက ကိုးကားဇာတ်ညွှန်းကို အသုံးပြုပါ။',
    speakingHowToPracticeStep4: 'အကြံပြုချက်ရရန် သင်တန်းအဆုံးသတ်ပြီး တိုးတက်မှုကို သိမ်းဆည်းပါ။',
    speakingNoConversations: 'ဤအဆင့်တွင် စကားပြောခန်းများ မရှိသေးပါ။',
    speakingStartPractice: 'လေ့ကျင့်ခန်း စတင်ရန်',
    speakingLoadingConversation: 'စကားပြောခန်း ဖွင့်နေသည်…',
    speakingPerson: 'ပုဂ္ဂိုလ်',
    speakingConversations: 'စကားပြောခန်းများ',
    speakingConversation: 'စကားပြောခန်း',
    speakingLevel: 'အဆင့်',
    speakingMin: 'မိနစ်',
    microphonePermissionError: 'မိုက်ခရိုဖုန်း ခွင့်ပြုချက်ကို ငြင်းဆိုခဲ့သည်။ အသံမှတ်တမ်းတင်ရန် မိုက်ခရိုဖုန်း ဝင်ရောက်ခွင့်ကို ပေးပါ။',
    submitting: 'ပို့နေသည်',
    startLesson: 'သင်ခန်းစာစတင်ရန်',
    viewProgress: 'တိုးတက်မှုကြည့်ရန်',
    
    // Reading
    readingExercise: 'စာဖတ်လေ့ကျင့်ခန်း',
    readPassage: 'အောက်ပါစာပိုဒ်ကို ဖတ်ပြီး မေးခွန်းများကို ဖြေဆိုပါ',
    readingChooseLevel: 'သင့်အဆင့် ရွေးချယ်ပါ',
    readingPickShelf: 'အဆင့်ဖြင့် စစ်ထုတ်ပြီး ခေါင်းစဉ်ရွေးကာ စတင်ပါ',
    readingAllLevels: 'အဆင့်အားလုံး',
    readingChooseLesson: 'သင်ခန်းစာ ရွေးချယ်ပါ',
    readingLesson: 'သင်ခန်းစာ',
    readingLessons: 'သင်ခန်းစာများ',
    readingStartLesson: 'သင်ခန်းစာ စတင်ရန်',
    readingNoLessons: 'ဤအဆင့်အတွက် သင်ခန်းစာ မတွေ့ပါ။',
    readingBackToLessons: 'သင်ခန်းစာများသို့ ပြန်ရန်',
    readingMinRead: 'မိနစ် ဖတ်ရန်',
    readingQuestionsCount: 'မေးခွန်း',
    readingStartTimer: 'စာဖတ်ချိန်မှတ်စတင်ရန်',
    readingPauseTimer: 'ချိန်မှတ်ရပ်ရန်',
    readingShowHistory: 'မှတ်တမ်းပြရန်',
    readingHide: 'ဖျောက်ရန်',
    readingPassageTitle: 'စာဖတ်စာပိုဒ်',
    vocabSelectHint: 'စာပိုဒ်ထဲက စကားလုံးကို နှိပ်ပြီး သင့်စာရင်းသို့ သိမ်းပါ။',
    readingOpenBook: 'စာအုပ်ဖွင့်ထား',
    readingNoPassage: 'ဤစာမျက်နှာတွင် စာပိုဒ်မရှိပါ',
    readingChooseAnother: 'ဆက်လက်ရန် အခြားလေ့ကျင့်ခန်း ဖွင့်ပါ',
    readingLoadNewExercise: 'လေ့ကျင့်ခန်း အသစ် ဖွင့်ရန်',
    readingSelected: 'ရွေးချယ်ထားသည်:',
    readingDismiss: 'ပယ်ဖျက်ရန်',
    readingComprehensionQuestions: 'နားလည်မှုမေးခွန်းများ',
    readingAnswerFromPassage: 'စာပိုဒ်မှ အဖြေရှာပါ — သဲလွန်စများကို သေချာစစ်ပါ',
    readingAnswered: 'ဖြေပြီး',
    readingMultipleChoice: 'ရွေးချယ်ဖြေဆိုမှု',
    readingMultipleChoiceDesc: 'စာပိုဒ်အပေါ် အခြေခံ၍ အကောင်းဆုံး ရွေးချယ်ပါ။',
    readingTrueFalse: 'မှန် / မှား',
    readingTrueFalseDesc: 'စာပိုဒ်နှင့် ကိုက်ညီမှု ဆုံးဖြတ်ပါ။',
    readingFillBlank: 'နေရာလွတ် ဖြည့်ပါ',
    readingFillBlankDesc: 'စာသားမှ အချက်အလက်ဖြင့် အဖြေရေးပါ။',
    readingQuestionSingular: 'မေးခွန်း',
    readingQuestionsPlural: 'မေးခွန်း',
    readingWriteAnswer: 'သင့်အဖြေ ရေးပါ...',
    readingChapterComplete: 'အခန်းပြီးပါပြီ',
    readingScoreExcellent: 'အလွန်ကောင်းပါတယ် — နောက်အဆင့်သို့ အဆင်သင့်ပါ!',
    readingScoreGood: 'တိုးတက်မှု ကောင်းပါတယ်။ ဆက်ဖတ်ပါ!',
    readingScoreKeep: 'ဆက်လက်လေ့ကျင့်ပါ — စာမျက်နှာတိုင်းမှာ တန်ဖိုးရှိပါတယ်။',
    readingBestScore: 'အကောင်းဆုံး အမှတ်:',
    readingErrorLoading: 'လေ့ကျင့်ခန်း ဖွင့်ရာတွင် အမှား',
    readingAttempt: 'ကြိုးစားမှု',
    readingResubmitTitle: 'ဤလေ့ကျင့်ခန်းကို ထပ်မံတင်မလား?',
    readingResubmitTimes: 'ကြိမ်',
    readingResubmitTime: 'ကြိမ်',
    readingResubmitAlready: 'ဤလေ့ကျင့်ခန်းကို သင် ကြိုးစားပြီးပါပြီ',
    readingResubmitBest: 'သင့်အကောင်းဆုံး အမှတ်မှာ',
    readingResubmitNote: 'ထပ်မံတင်ခြင်းဖြင့် ကြိုးစားမှု အသစ်ဖြစ်ပြီး တိုးတက်မှုကို မွမ်းမံပါမည်။',
    readingGoBack: 'နောက်သို့',
    readingSubmitAnyway: 'ဘာဖြစ်ဖြစ် တင်ပါ',
    readingTrue: 'မှန်',
    readingFalse: 'မှား',
    question: 'မေးခွန်း',
    of: 'ထဲမှ',
    correctAnswer: 'မှန်ပါတယ်！',
    incorrectAnswer: 'မှားပါတယ်',
    yourScore: 'သင့်အမှတ်',
    
    // Writing
    writingPrompt: 'စာအရေးအကြံပြုချက်',
    writingChooseLevel: 'သင့်အဆင့် ရွေးချယ်ပါ',
    writingPickShelf: 'အဆင့်ဖြင့် စစ်ထုတ်ပြီး ခေါင်းစဉ်ရွေးကာ စတင်ပါ',
    writingChooseLesson: 'သင်ခန်းစာ ရွေးချယ်ပါ',
    writingLesson: 'သင်ခန်းစာ',
    writingLessons: 'သင်ခန်းစာများ',
    writingStartLesson: 'စာရေး စတင်ရန်',
    writingNoLessons: 'ဤအဆင့်အတွက် သင်ခန်းစာ မတွေ့ပါ။',
    writingBackToLessons: 'သင်ခန်းစာများသို့ ပြန်ရန်',
    writeResponse: 'သင့်အဖြေကို အောက်တွင်ရေးပါ',
    wordCount: 'စာလုံးအရေအတွက်',
    minWords: 'အနည်းဆုံးစာလုံးအရေအတွက်',
    submitWriting: 'စာအရေးတင်သွင်းရန်',
    allLevels: 'အဆင့်အားလုံး',
    yourResponse: 'သင့်အဖြေ',
    writeResponsePlaceholder: 'သင့်အဖြေကို ဤနေရာတွင် ရေးပါ...',
    wordsLabel: 'စာလုံးများ',
    sentencesLabel: 'ဝါကျများ',
    minWordsSuggested: 'အနည်းဆုံး စာလုံး {count} လုံး အကြံပြုသည်',
    showExampleAnswer: 'ဥပမာ အဖြေကို ပြရန်',
    previousAttempts: 'ယခင်ကြိုးစားမှုများ',
    bestScoreLabel: 'အကောင်းဆုံးအမှတ်',
    submitForGrade: 'အမှတ်ပေးရန်နှင့် တုံ့ပြန်ချက်ရရန် တင်သွင်းပါ',
    gradingWriting: 'သင့်စာအရေးကို အမှတ်ပေးနေသည်…',
    aiReadingEssay: 'AI က သင့်စာစီစာကုံးကို ဖတ်နေသည် — တစ်မိနစ်အထိ ကြာနိုင်သည်…',
    writingSaved: 'သင့်စာအရေးကို အကောင့်တွင် သိမ်းဆည်းပြီးပါပြီ',
    attemptLabel: 'ကြိုးစားမှု',
    yourGrade: 'သင့်အမှတ်',
    excellentWriting: 'အလွန်ကောင်းသော စာအရေး — နောက်သင်ခန်းစာအတွက် အဆင်သင့်!',
    goodWorkKeepPracticing: 'ကောင်းပါသည်။ ဆက်လက်လေ့ကျင့်ပါ!',
    keepWritingDraft: 'ဆက်ရေးပါ — မူကြမ်းတိုင်းက သင့်ကို တိုးတက်စေသည်။',
    scoreFromAi: 'AI ဆရာထံမှ အမှတ်',
    scoreFromLength: 'အရှည်နှင့် ဖွဲ့စည်းပုံအပေါ် အခြေခံသော အမှတ်',
    teacherFeedback: 'ဆရာ၏ တုံ့ပြန်ချက်',
    gradedByAi: 'AI က အမှတ်ပေးသည်',
    gradedAutomatically: 'အလိုအလျောက် အမှတ်ပေးသည်',
    rubricTask: 'တာဝန်',
    rubricGrammar: 'သဒ္ဒါ',
    rubricVocabulary: 'ဝေါဟာရ',
    rubricOrganization: 'ဖွဲ့စည်းမှု',
    strengths: 'အားသာချက်များ',
    howToImprove: 'တိုးတက်အောင် လုပ်နည်း',
    grammarTips: 'သဒ္ဒါအကြံပြုချက်များ',
    writingResubmitTitle: 'ဤစာအရေးကို ထပ်မံတင်မလား?',
    
    // Profile
    bio: 'မိမိကိုယ်ရေး',
    nativeLanguage: 'မိခင်ဘာသာစကား',
    englishLevel: 'အင်္ဂလိပ်စာရည်ရွယ်ချက်',
    learningGoal: 'သင်ယူမှုပန်းတိုင်',
    profilePicture: 'ပရိုဖိုင်ပုံ',
    chooseAvatar: 'ပုံရွေးချယ်ရန်',
    removePhoto: 'ပုံကို ဖျက်ရန်',
    personalInfoSaved: 'ကိုယ်ရေးကိုယ်တာ အချက်အလက်များ သိမ်းဆည်းပြီးပါပြီ!',
    learningProfileSaved: 'သင်ယူမှု ပရိုဖိုင် သိမ်းဆည်းပြီးပါပြီ!',
    oopsSomethingWentWrong: 'အိုး! တစ်ခုခု မှားယွင်းနေပါတယ်',
    loadingYourProfile: 'သင့်ပရိုဖိုင်ကို ဖွင့်နေသည်...',
    thisWontTakeLong: 'ဒါက အချိန်မကြာပါဘူး',
    personalInformation: 'ကိုယ်ရေးကိုယ်တာ အချက်အလက်များ',
    enterFirstName: 'အမည် ထည့်သွင်းပါ',
    enterLastName: 'အမျိုးအမည် ထည့်သွင်းပါ',
    saving: 'သိမ်းဆည်းနေသည်...',
    savePersonalInformation: 'ကိုယ်ရေးကိုယ်တာ အချက်အလက်များ သိမ်းဆည်းရန်',
    learningProfile: 'သင်ယူမှု ပရိုဖိုင်',
    tellUsAboutJourney: 'သင့်သင်ယူမှုခရီးအကြောင်း ပြောပြပါ...',
    selectLevel: 'အဆင့် ရွေးချယ်ရန်',
    beginner: 'စတင်သူ',
    intermediate: 'အလယ်အလတ်',
    advanced: 'အဆင့်မြင့်',
    egSpanish: 'ဥပမာ၊ စပိန်',
    egPassTOEFL: 'ဥပမာ၊ TOEFL စာမေးပွဲ အောင်ရန်',
    dailyGoal: 'နေ့စဉ် ပန်းတိုင်',
    saveLearningProfile: 'သင်ယူမှု ပရိုဖိုင် သိမ်းဆည်းရန်',
    learningProgress: 'သင်ယူမှု တိုးတက်မှု',
    setYourLearningObjective: 'သင့်သင်ယူမှု ရည်မှန်းချက်ကို သတ်မှတ်ပါ',
    dailyMinutes: 'နေ့စဉ် မိနစ်များ',
    level: 'အဆင့်',
    ofDailyGoal: '% နေ့စဉ် ပန်းတိုင်',
    welcomeToJourney: 'သင့်ဘာသာစကား သင်ယူမှုခရီးသို့ ကြိုဆိုပါတယ်! သင့်ပန်းတိုင်များကို သတ်မှတ်ပြီး တိုးတက်မှုကို ခြေရာခံပါ။',
    memberSince: 'အဖွဲ့ဝင်ဖြစ်သည့်နေ့',
    accountStatus: 'အကောင့် အခြေအနေ',
    accountStatusActive: 'အသက်ဝင်',
    accountStatusInactive: 'မသက်ဝင်',
    userId: 'အသုံးပြုသူ ID',
    profileId: 'ပရိုဖိုင် ID',
    adminBadge: 'စီမံခန့်ခွဲသူ',
    profileAccountReadOnlyNote: 'အီးမေးလ်နှင့် အသုံးပြုသူအမည်သည် သင့်အကောင့်မှ လာပါသည်။ ဤစာမျက်နှာတွင် ပြင်ဆင်မရပါ။',
    nationality: 'နိုင်ငံသား',
    currentLocation: 'လက်ရှိ နေရာ',
    selectCountry: 'နိုင်ငံ ရွေးချယ်ပါ',
    searchCountry: 'နိုင်ငံများ ရှာရန်',
    clearCountry: 'ရွေးချယ်မှု ဖယ်ရှားရန်',
    profilePersonalInfoHint: 'သင့်အမည်၊ နိုင်ငံသားနှင့် လက်ရှိနေရာကို မွမ်းမံပါ။',
    profileLearningProgressHint: 'သင့်ရည်မှန်းချက်များနှင့် နေ့စဉ် လေ့လာချိန်။',
    profileLearningProfileHint: 'ပလက်ဖောင်းတွင် သင်ယူပုံကို စိတ်ကြိုက်ပြင်ဆင်ပါ။',
    
    // Activities
    yourLanguageJourney: 'သင့်ဘာသာစကားလေ့လာမှုခရီး',
    trackProgressCelebrate: 'တိုးတက်မှုကိုစောင့်ကြည့်ရန်, ေအာင်မြင်ရန်, သင်ယူမှုဆက်လက်တိုးတက်ရန်',
    totalActivities: 'စုစုပေါင်းလုပ်ဆောင်ချက်များ',
    dayStreak: 'ရက်လိုက်အစဉ်လိုက်',
    master: 'ကျွမ်းကျင်',
    good: 'ေကာင်းပါတယ်',
    gettingStarted: 'စတင်လိုက်ပါ',
    completed: 'ပြီးဆုံးသောသင်ခန်းစာများ',
    lessonsStarted: 'စတင်ခဲ့သောသင်ခန်းစာများ',
    profileUpdates: 'ပရိုဖိုင်အသစ်မွမ်းမံမှု',
    totalLogins: 'ဝင်ရောက်မှုအရေအတွက်',
    recentActivities: 'လတ်တလောလုပ်ဆောင်ချက်များ',
    latestLearningMilestones: 'ေနာက်ဆုံးသင်ယူမှုမှတ်တိုင်',
    allActivities: 'လုပ်ဆောင်ချက်အားလုံး',
    completedLessons: 'ပြီးဆုံးသောသင်ခန်းစာများ',
    startedLessons: 'စတင်ခဲ့သောသင်ခန်းစာများ',
    profileUpdatesFilter: 'ပရိုဖိုင်အသစ်မွမ်းမံမှုများ',
    logins: 'ဝင်ရောက်မှုအရေအတွက်',
    noActivitiesFound: 'လုပ်ဆောင်ချက်မရှိသေးပါ',
    tryChangingFilter: 'အမျိုးအစားကိုပြောင်းလဲကြည့်ရန်',
    activityId: 'လုပ်ဆောင်ချက်အမှတ်',
    type: 'အမျိုးအစား',
    timestamp: 'အချိန်အမှတ်',
    readyForMore: 'အဆင်သင့်ဖြစ်လား？',
    continueLearningJourney: 'ဆက်လက်လေ့လာရန်',
    exploreMoreActivities: 'ပိုမိုလေ့လာရန်',
    pageLabel: 'စာမျက်နှာ',
    previous: 'ယခင်',
    next: 'ရှေ့',
    activityTypeLessonStarted: 'သင်ခန်းစာ စတင်ခဲ့သည်',
    activityTypeLessonCompleted: 'သင်ခန်းစာ ပြီးစီးခဲ့သည်',
    activityTypeProfileUpdated: 'ပရိုဖိုင် ပြင်ဆင်ခဲ့သည်',
    activityTypeLogin: 'ဝင်ရောက်မှု',
    activityTypeWritingPractice: 'စာရေးလေ့ကျင့်ခန်း',
    activityTypeReadingPractice: 'စာဖတ်လေ့ကျင့်ခန်း',
    activityTypeSpeakingPractice: 'စကားပြောလေ့ကျင့်ခန်း',
    activityTypeListeningPractice: 'နားထောင်လေ့ကျင့်ခန်း',
    activityTypeVocabularySaved: 'စကားလုံး သိမ်းပြီး',
    activityTypeVocabularyReviewed: 'စကားလုံး ပြန်လည်သုံးသပ်',
    activityTypeVocabularyQuiz: 'စကားလုံး စစ်ဆေးချက်',
    vocabularyFilter: 'စကားလုံး',
    activityTypeDefault: 'လုပ်ဆောင်ချက်',
    activityDetails: 'အသေးစိတ်',
    expandActivity: 'ချဲ့ကြည့်ရန်',
    collapseActivity: 'ဖျောက်ရန်',
    
    // Settings
    settingsSubtitle: 'သင့်သင်ယူမှုအတွေ့အကြုံကို စိတ်ကြိုက်ပြင်ဆင်ပါ',
    focusArea: 'အာရုံစိုက်ရမည့်နေရာ',
    focusAreaDescription: 'ဒက်ရှ်ဘုတ်တွင် ဦးစားပေး လေ့ကျင့်မည့်အရာကို ရွေးပါ။',
    focusAreaBalanced: 'မျှတမှု',
    focusAreaBalancedHint: 'အနည်းဆုံးကျွမ်းကျင်မှုကို အလိုအလျောက် အကြံပြု',
    focusAreaReadingHint: 'စာဖတ်ကို ဒက်ရှ်ဘုတ်တွင် ဦးစားပေး',
    focusAreaWritingHint: 'စာရေးကို ဒက်ရှ်ဘုတ်တွင် ဦးစားပေး',
    focusAreaListeningHint: 'နားထောင်ကို ဒက်ရှ်ဘုတ်တွင် ဦးစားပေး',
    focusAreaSpeakingHint: 'စကားပြောကို ဒက်ရှ်ဘုတ်တွင် ဦးစားပေး',
    focusAreaGrammarHint: 'သဒ္ဒါကို ဒက်ရှ်ဘုတ်တွင် ဦးစားပေး',
    focusAreaSaved: 'သင်ယူမှု စိတ်ကြိုက်များ သိမ်းပြီး',
    saveDailyGoal: 'နေ့စဉ်ပန်းတိုင် သိမ်းရန်',
    dailyGoalSettingsHint: 'ဒက်ရှ်ဘုတ်တွင် ယနေ့ လေ့လာမှု ပစ်မှတ်အဖြစ် ပြသသည်။',
    viewDashboard: 'ဒက်ရှ်ဘုတ် ဖွင့်ရန်',
    loading: 'ဖွင့်နေသည်…',
    preferences: 'အကြိုက်များ',
    currentTheme: 'လက်ရှိအပြင်အဆင်',
    currentLanguage: 'လက်ရှိဘာသာစကား',
    fontSize: 'စာလုံးအရွယ်အစား',
    fontSizeDescription: 'အက်ပ်တစ်ခုလုံးအတွက် သက်တောင့်သက်သာ ဖတ်ရှုနိုင်သော အရွယ်အစားကို ရွေးချယ်ပါ။',
    fontSizeSmall: 'သေး',
    fontSizeMedium: 'အလယ်အလတ်',
    fontSizeLarge: 'ကြီး',
    currentFontSize: 'လက်ရှိ စာလုံးအရွယ်အစား',
    preview: 'အစမ်းမြင်ကွင်း',
    previewDescription: 'ဤအစမ်းမြင်ကွင်းသည် သင့်ဆက်တင်များကို အက်ပ်တစ်ခုလုံးတွင် မည်သို့ပေါ်လာမည်ကို ပြသပါမည်။',
    
    // Footer
    home: 'ပင်မစာမျက်နှာ',
    navigation: 'လမ်းညွှန်ချက်',
    support: 'ပံ့ပိုးမှု',
    contactUs: 'ဆက်သွယ်ရန်',
    allRightsReserved: 'မူပိုင်ခွင့်အားလုံး လက်၀ယ်ရရှိထားပါသည်။',
    termsAndConditions: 'စည်းကမ်းချက်များ',
    footerDescription: 'အပြန်အလှန်လေ့ကျင့်ခန်းများနှင့် ကိုယ်ပိုင်သင်ယူမှုလမ်းကြောင်းများဖြင့် အင်္ဂလိပ်စာကို ကျွမ်းကျင်အောင် လေ့ကျင့်ပါ။',

    // Home Page
    startLearningFree: 'အခမဲ့စတင်လေ့လာရန်',
    goToDashboard: 'ဒက်ရှ်ဘုတ်သို့သွားရန်',
    homeBadge: 'ကမ္ဘာတစ်ဝှမ်းရှိ သင်ယူသူများက ယုံကြည်စိတ်ချရသော',
    homeTitle1: 'သင့်နှင့်အတူ တည်တံ့သော အင်္ဂလိပ်စာ လေ့ကျင့်မှု။',
    homeTitle2: 'အဆင့်မြင့်တင်လိုက်ပါ',
    homeTitle3: 'တိုးတက်မှု ရလဒ်',
    homeHeroLead: 'ယခု လေ့ကျင့်နေသည်',
    homeHeroLabelReading: 'အဖတ် — စာကြောင်းတိုင်းမှ အဓိပ္ပာယ်ကို မြင်ပါ',
    homeHeroLabelWriting: 'အရေး — အတွေးများကို ရှင်းလင်းသော စကားလုံးများအဖြစ် ပုံဖော်ပါ',
    homeHeroLabelListening: 'နားထောင်ခြင်း — အသံနှင့် အရိပ်အမြွက်တိုင်းကို ဖမ်းယူပါ',
    homeHeroLabelSpeaking: 'အပြော — သင့်အသံကို လွယ်ကူစွာ ရှာဖွေပါ',
    homeBillboardLead: 'လေ့ကျင့်ရန် နေရာ',
    homeHeroKicker: 'ကျွမ်းကျင်မှု လေးခု၊ ခရီးတစ်ခု',
    homeHeroLine: 'လေ့ကျင့်ရန် နေရာ',
    homeHeroTo: '',
    homeBillboardReading: 'ဖတ်',
    homeBillboardWriting: 'ရေး',
    homeBillboardListening: 'နား',
    homeBillboardSpeaking: 'ပြော',
    homeSubtitle: 'ပိုမိုကောင်းမွန်သော အင်္ဂလိပ်စာ ခရီးကို ဒီမှာ စတင်ပါ',
    homeHeroStory: 'အင်္ဂလိပ်စာကို ကျွမ်းကျင်လာရင်း ဇာတ်လမ်းကမ္ဘာကို လေ့လာပါ။ ဇာတ်လမ်းများ ရှာဖွေပါ။ အင်္ဂလိပ်စာ တိုးတက်အောင် လုပ်ပါ။ ပိုဖတ်ပါ။ ပိုသင်ယူပါ။ ယုံကြည်မှုဖြင့် ပြောပါ။',
    homeHeroStatLessons: 'သင်ခန်းစာ',
    homeHeroStatSkills: 'အဓိက ကျွမ်းကျင်မှု',
    homeHeroStatAnytime: 'အချိန်မရွေး လေ့လာနိုင်',
    homeHeroStatLevels: 'သင်ယူမှု အဆင့်များ',
    homeHeroStatLanguages: 'ဘာသာစကား',
    homeHeroStatFree: 'စတင်နိုင်',
    homeHeroStatVocab: 'ဝေါဟာရ စကားလုံးများ',
    homeHeroStatExercises: 'အပြန်အလှန် လေ့ကျင့်ခန်း',
    homeHeroStatGrammar: 'သဒ္ဒါ လေ့ကျင့်ခန်း',
    homeHeroStatTopics: 'သင်ယူမှု ခေါင်းစဉ်',
    homeHeroStatPersonal: 'တစ်ဦးချင်း လေ့လာမှု',
    homeHeroStatDaily: 'နေ့စဉ် လေ့ကျင့်ခန်း',
    homeHeroStatPlatform: 'တစ်နေရာတည်း လေ့လာမှု',
    homeTrust1: 'အမြဲအခမဲ့',
    homeTrust2: 'ကတ်မလိုအပ်',
    homeTrust3: 'စက္ကန့် ၃၀ အတွင်း စတင်ပါ',
    homeFeaturesTitle: 'အဓိကအရည်အချင်း လေးခုလုံးကို ကျွမ်းကျင်အောင် လေ့လာလိုက်ပါ',
    homeFeaturesSubtitle: 'ကျွမ်းကျင်မှု ဖွံ့ဖြိုးတိုးတက်မှုမှတစ်ဆင့် ပြီးပြည့်စုံသော ဘာသာစကား သင်ယူခြင်း',
    homeEverythingTitle: 'လိုအပ်သမျှ အားလုံး။',
    homeEverythingSubtitle: 'ပိုမလိုပါ။ ရှေ့သို့ တိုးစေသော လေ့ကျင့်မှုသာ။',
    homeMarqueeExercises: 'လေ့ကျင့်ခန်း ၅၀+',
    homeMarqueeSkills: 'အဓိက ကျွမ်းကျင်မှု ၄',
    homeMarqueeLanguages: 'ဘာသာစကား ၃',
    homeMarqueeFree: '၁၀၀% အခမဲ့',
    homePickSkill: 'ကျွမ်းကျင်မှု ရွေးပါ',
    homeReadingDesc: 'သင့်အဆင့်နှင့် ကိုက်ညီသော စိတ်ဝင်စားစရာ လေ့ကျင့်ခန်းများဖြင့် နားလည်မှု၊ ဝေါဟာရ ချဲ့ထွင်မှု၊ စာသားများ ခွဲခြမ်းစိတ်ဖြာမှုကို မြှင့်တင်ပါ။',
    homeWritingDesc: 'ဖွဲ့စည်းထားသော လေ့ကျင့်ခန်းများမှတစ်ဆင့် စိတ်ကူးများကို ရှင်းလင်းစွာ ဖော်ပြပါ၊ သဒ္ဒါကို ကျွမ်းကျင်အောင် လေ့လာပါ၊ သင့်ရေးသားမှု စတိုင်ကို ဖွံ့ဖြိုးစေပါ။',
    homeListeningDesc: 'တကယ့်ကမ္ဘာ့ အကြောင်းအရာများဖြင့် အသံထွက်၊ စကားပြောပုံစံ နားလည်မှု၊ အသံ နားလည်မှုကို မြှင့်တင်ပါ။',
    homeSpeakingDesc: 'အပြန်အလှန် ပြောဆိုခြင်း လေ့ကျင့်ခန်းများမှတစ်ဆင့် ယုံကြည်မှု တည်ဆောက်ပါ၊ စကားပြောဆိုမှု လေ့ကျင့်ပါ၊ ကျွမ်းကျင်မှု ရရှိပါ။',
    homeHowItWorksTitle: 'အသုံးပြုပုံ',
    homeHowItWorksSubtitle: 'အဆင့် သုံးဆင့်ဖြင့် စတင်ပါ',
    homeStep1Title: 'အကောင့်ဖွင့်ပါ',
    homeStep1Desc: 'ချက်ချင်း စတင်နိုင်ပါသည်',
    homeStep2Title: 'သင့်ကျွမ်းကျင်မှုကို ရွေးချယ်ပါ',
    homeStep2Desc: 'ဖတ်ခြင်း၊ ရေးခြင်း၊ နားထောင်ခြင်း၊ သို့မဟုတ် ပြောခြင်းကို ရွေးချယ်ပါ။ ကျွမ်းကျင်မှု တစ်ခုစီတွင် သင့်အဆင့်နှင့် ကိုက်ညီသော လေ့ကျင့်ခန်းများ ရှိပါသည်။',
    homeStep3Title: 'လေ့ကျင့်ရန် စတင်ပါ',
    homeStep3Desc: 'သင့်ကျွမ်းကျင်မှု အဆင့်အတွက် ဒီဇိုင်းထုတ်ထားသော လေ့ကျင့်ခန်းများဖြင့် စတင်ပါ။ တိုးတက်မှုကို ခြေရာခံပြီး နေ့စဉ် မြှင့်တင်ပါ။',
    homeWhyChooseTitle: 'ဘာကြောင့် ကျွန်ုပ်တို့ကို ရွေးချယ်ရမလဲ',
    homeWhyChooseSubtitle: 'ကျွန်ုပ်တို့၏ ပလက်ဖောင်းကို ထူးခြားစေသော အရာများ',
    homeBenefit1Title: 'ကိုယ်ပိုင် သင်ယူမှု',
    homeBenefit1Desc: 'လေ့ကျင့်ခန်းများသည် သင့်ကျွမ်းကျင်မှု အဆင့်နှင့် ကိုက်ညီအောင် ညှိပေးပြီး၊ အဆင့်တိုင်းတွင် အကောင်းဆုံး စိန်ခေါ်မှုနှင့် ကြီးထွားမှုကို သေချာစေပါသည်။',
    homeBenefit2Title: 'တိုးတက်မှု ခြေရာခံခြင်း',
    homeBenefit2Desc: 'အသေးစိတ် ခွဲခြမ်းစိတ်ဖြာမှု၊ အစဉ်လိုက်၊ ပြီးပြည့်စုံသော တိုးတက်မှု အစီရင်ခံစာများဖြင့် သင့်တိုးတက်မှုကို စောင့်ကြည့်ပါ။',
    homeBenefit3Title: 'ဘာသာစကား များစွာ ပံ့ပိုးမှု',
    homeBenefit3Desc: 'အင်္ဂလိပ်၊ မြန်မာ နှင့် တရုတ် ဘာသာစကားများဖြင့် ပြီးပြည့်စုံသော အင်တာဖေ့စ် ပံ့ပိုးမှု၊ သင်ယူမှုကို လက်လှမ်းမီစေပြီး သက်တောင့်သက်သာ ဖြစ်စေပါသည်။',
    homeBenefit4Title: 'ချက်ချင်း အကြံပြုချက်',
    homeBenefit4Desc: 'လေ့ကျင့်ခန်း တစ်ခုစီအတွက် ချက်ချင်း ရလဒ်များနှင့် ရှင်းလင်းချက်များကို ရယူပါ၊ အမှားများမှ သင်ယူရန် ကူညီပေးပါသည်။',
    homeBenefit5Title: 'ဖွဲ့စည်းထားသော သင်ရိုးညွှန်းတမ်း',
    homeBenefit5Desc: 'စတင်သူမှ အဆင့်မြင့်အထိ ရှင်းလင်းသော သင်ယူမှု လမ်းကြောင်းကို လိုက်နာပါ၊ တစ်ခုနှင့်တစ်ခု တည်ဆောက်ထားသော လေ့ကျင့်ခန်းများဖြင့်။',
    homeBenefit6Title: 'ပြောင်းလွယ်သော လေ့ကျင့်မှု',
    homeBenefit6Desc: 'သင့်ကိုယ်ပိုင် အရှိန်ဖြင့်၊ မည်သည့်အချိန်တွင်မဆို၊ မည်သည့်နေရာတွင်မဆို သင်ယူပါ။ သင့်အချိန်ဇယားနှင့် ကိုက်ညီသောအချိန်တွင် လေ့ကျင့်ပါ။',
    homeStat1: 'အပြန်အလှန် လေ့ကျင့်ခန်းများ',
    homeStat2: 'အဓိက ကျွမ်းကျင်မှုများ',
    homeStat3: 'ဘာသာစကားများ',
    homeStat4: 'အခမဲ့ ဝင်ရောက်ခွင့်',
    homeCTATitle: 'သင့်ခရီးကို စတင်ရန် အဆင်သင့်ဖြစ်ပြီလား?',
    homeCTASubtitle: 'နေ့စဉ် အင်္ဂလိပ်စာ သင်ယူေနသူများနှင့် ပူးပေါင်းလိုက်ပါ။ အခမဲ့ စတင်လိုက်ပါ။',
    homeOrbitLabel: 'သင်ယူမှု လမ်းကြောင်း',
    homeOrbitCenter: 'ကျွမ်းကျင်မှု ၄ - ခရီး ၁',
    homePreviewLabel: 'တိုက်ရိုက် အစမ်းမြင်ကွင်း',
    homePreviewReading1: 'စူးစမ်းရေးအဖွဲ့သည် နေထွက်မီ ချိုင့်ဝှမ်းကို ဖြတ်သန်းခဲ့သည်။',
    homePreviewReading2a: 'သူတို့သည် ',
    homePreviewReadingHighlight: 'ရှေးဟောင်းတောင်ကုန်းကို',
    homePreviewReading2b: ' အရုဏ်ဦးတွင် ရောက်ရှိခဲ့သည်။',
    homePreviewWritingDraft: 'ကျွန်တော် နည်းပညာတွင် သုံးနှစ်ကြာ အလုပ်လုပ်နေတယ်။',
    homePreviewWritingFix: 'ကျွန်တော် နည်းပညာလောကတွင် သုံးနှစ်ကြာ အလုပ်လုပ်နေပါသည်။',
    homePreviewListening: 'နားထောင်ပြီး စာကြောင်းကို ပြီးမြောက်အောင် လုပ်ပါ',
    homePreviewSpeakingQ: 'စက္ကန့် ၃၀ အတွင်း မိမိကိုယ်ကို မိတ်ဆက်ပါ။',
    homePreviewSpeakingA: '"မင်္ဂလာပါ၊ ကျွန်မက မာယာ — သင်ယူမှုကိရိယာများ ဒီဇိုင်းရေးဆွဲသူပါ။"',
    homePreviewMetric: 'အတိအကျမှု',
    homeExploreSkills: 'ကျွမ်းကျင်မှုတစ်ခုချင်း လေ့ကျင့်ပါ',
    homePracticeSkill: 'ဤကျွမ်းကျင်မှုကို လေ့ကျင့်ပါ',
    
    // Common additional
    nextLanguage: 'ေနာက်ဘာသာစကား',
    errorOccurred: 'တစ်ခုခုမှားနေပါသည်။ ကျေးဇူးပြု၍ ထပ်ကြိုးစားကြည့်ပါ။',
    reloadPage: 'စာမျက်နှာ ပြန်လည်ဖွင့်ရန်',
    pauseAudio: 'အသံကို ရပ်ရန်',
    playAudio: 'အသံကို ဖွင့်ရန်',
    feedback: 'အကြံပြုချက်',
    submitSuggestion: 'အကြံပြုချက်',
    manageFeedback: 'တုံ့ပြန်ချက် စီမံခန့်ခွဲရန်',
    haveSuggestion: 'အကြံပြုချက် ရှိပါသလား?',
  },

  zh: {
    dashboard: '仪表盘',
    skills: '技能',
    reading: '阅读',
    writing: '写作',
    listening: '听力',
    speaking: '口语',
    vocabulary: '词汇',
    grammar: '语法',
    grammarPractice: '练习',
    grammarPageLead: '先学一种形式，再混合练习。能选对时态，才算掌握。',
    grammarComingSoon: '即将推出',
    grammarItems: '题',
    grammarCheck: '核对答案',
    grammarScore: '得分',
    grammarTryAgain: '再试一次',
    grammarBackToTopics: '全部主题',
    grammarHint: '提示',
    grammarExplanation: '讲解',
    grammarPerson: '人称',
    grammarPositive: '肯定',
    grammarNegative: '否定',
    grammarQuestion: '疑问',
    grammarGroup_present: '现在',
    grammarGroup_past: '过去',
    grammarGroup_future: '将来',
    grammarGroup_articles: '冠词',
    grammarGroup_agreement: '主谓一致',
    grammarGroup_prepositions: '介词',
    grammarGroup_comparatives: '比较级',
    grammarGroup_conditionals: '条件句',
    grammarGroup_modals: '情态动词',
    grammarGroup_reported: '间接引语',
    grammarGroup_passives: '被动语态',
    grammarGroup_mastery: '综合练习',
    grammarMastered: '已掌握',
    grammarCompleted: '已完成',
    grammarBest: '最好成绩',
    grammarAnswer: '答案',
    grammarRetryMissed: '练习错题',
    grammarRetryMissedHint: '只练你错过的题，做对的会保留。',
    grammarNextTopic: '下一题组',
    grammarRecommended: '建议下一步',
    grammarMasteryCount: '已完成 {n} / {total}',
    activities: '活动',
    profile: '个人资料',
    settings: '设置',
    login: '登录',
    signup: '注册',
    logout: '退出',
    adminPanel: '管理面板',
    welcome: '欢迎',
    tryAgain: '重试',
    welcomeBack: '欢迎回来',
    readyToContinue: '准备好继续您的学习之旅了吗？',
    lessonsCompleted: '已完成课程',
    timeSpent: '学习时长',
    averageScore: '平均分数',
    learningStreak: '连续学习',
    skillsProgress: '技能进度',
    recentActivity: '最近活动',
    noRecentActivity: '暂无最近活动',
    minutes: '分钟',
    days: '天',
    loadingCosmicJourney: '正在加载您的学习之旅...',
    somethingWentWrong: '出了点问题',
    yourLearningDashboard: '您的学习仪表盘',
    continueCosmicJourney: '继续您的学习之旅，探索知识的新领域',
    trackYourMastery: '跟踪各项技能的掌握程度',
    noSkillsDataAvailable: '暂无技能数据',
    dashboardSubtitle: '一目了然地查看进度，继续学习之旅。',
    dashboardWeeklyActivity: '本周',
    dashboardWeeklyActivityHint: '每日完成的课程数',
    dashboardQuickPractice: '快速练习',
    dashboardQuickPracticeHint: '直接进入某项技能',
    dashboardFocusArea: '重点提升',
    dashboardCustomize: '自定义',
    dashboardDailyGoal: '今日目标',
    dashboardDailyGoalMet: '已达成今日目标 — 很棒！',
    dashboardDailyGoalHint: '今日目标已完成 {pct}%',
    dashboardYourFocus: '您的重点',
    dashboardFocusFromSettings: '来自设置中的偏好',
    dashboardFocusAutoWeakest: '根据最弱技能自动建议',
    dashboardPickFocus: '在设置中选择重点',
    dashboardFocusTag: '重点',
    dashboardStartedShort: '已开始',
    dashboardRecentActivity: '最近活动',
    dashboardRecentActivityHint: '账户中的最新学习动态',
    dashboardNoRecentActivity: '暂无最近活动 — 开始一节课吧。',
    dashboardStrongestSkill: '最强技能',
    dashboardViewActivities: '查看全部活动',
    dashboardLessonsThisWeek: '本周课程',
    dashboardActiveDays: '活跃天数',
    dashboardNoWeeklyData: '本周尚未完成课程。今天就开始练习吧。',
    dashboardPracticeNow: '立即练习',
    dashboardOverallProgress: '总体进度',
    dashboardStreakActive: '保持连续学习',
    dashboardStreakStart: '开始一节课或复习词汇以开启连续学习',
    dashboardVocabLearned: '已学单词',
    dashboardVocabOfSaved: '共保存 {total} 个',
    dashboardVocabInProgress: '{n} 个仍在学习中',
    dashboardVocabNoneSaved: '保存你的第一个单词',
    appearance: '外观',
    language: '语言',
    theme: '主题',
    lightMode: '浅色模式',
    darkMode: '深色模式',
    systemMode: '跟随系统',
    selectLanguage: '选择语言',
    english: 'English',
    burmese: 'မြန်မာ',
    chinese: '中文',
    email: '邮箱',
    password: '密码',
    showPassword: '显示密码',
    hidePassword: '隐藏密码',
    confirmPassword: '确认密码',
    firstName: '名',
    lastName: '姓',
    username: '用户名',
    signIn: '登录',
    signUp: '注册',
    createAccount: '创建账户',
    creatingAccount: '正在创建账户',
    signingIn: '正在登录',
    joinUsToGetStarted: '加入我们，开始学习',
    alreadyHaveAccount: '已有账户？',
    dontHaveAccount: '还没有账户？',
    forgotPassword: '忘记密码？',
    forgotPasswordLead: '输入账户邮箱。如果已注册，我们将发送重置链接。',
    forgotPasswordSubmit: '发送重置链接',
    forgotPasswordSent: '如果该邮箱已注册，你很快会收到重置链接。请查看收件箱。',
    sendingReset: '正在发送',
    backToSignIn: '返回登录',
    resetPassword: '重置密码',
    resetPasswordLead: '为账户设置新密码。',
    resetPasswordSubmit: '更新密码',
    resetPasswordSuccess: '密码已更新。现在可以登录。',
    resetPasswordInvalid: '此重置链接无效或已过期。请重新申请。',
    passwordsDoNotMatch: '两次输入的密码不一致。',
    newPassword: '新密码',
    confirmNewPassword: '确认新密码',
    authLibraryPortal: '图书馆入口',
    authNewRegistration: '新用户注册',
    authWelcomeBack: '欢迎回来',
    authLoginTagline: '在平静专注的空间里，继续你的英语之旅。',
    authLoginFormHint: '登录后按自己的节奏继续学习。',
    authJoinHeadline: '开启你的旅程',
    authSignupTagline: '在温暖的空间里提升读、写、听、说。',
    authSignupFormHint: '创建免费账户 — 只需片刻。',
    dismiss: '关闭',
    selectSkill: '选择技能',
    lessons: '课程',
    difficulty: '难度',
    listeningPractice: '听力练习',
    improveYourListeningSkills: '提升您的听力技能',
    listenToTheAudioAndAnswerQuestions: '听音频并回答以下问题',
    listeningChooseLevel: '选择级别',
    listeningPickShelf: '按级别筛选，再按音频标题选择课程',
    listeningChooseLesson: '选择听力课程',
    listeningLesson: '课程',
    listeningLessons: '课程',
    listeningStartLesson: '开始听力',
    listeningNoLessons: '该级别没有听力课程。',
    listeningBackToLessons: '返回课程列表',
    listeningHasAudio: '音频',
    listeningVoiceOnly: '语音',
    listeningMin: '分钟',
    listeningQuestionsCount: '题',
    listeningBooth: '听力室',
    listeningNowPlaying: '正在播放…',
    listeningPressPlay: '准备好后点击播放',
    listeningAnswerHint: '仔细听，然后选择最佳答案',
    listeningAnswered: '已答',
    listeningAudioUnavailable: '音频不可用，且此设备不支持语音合成。',
    listeningTtsFallback: '使用语音合成 — 此练习没有可用音频文件。',
    listeningClassroom: '听力课堂',
    listeningTeacherNote: '任务',
    listeningStepListen: '听音',
    listeningStepAnswer: '作答',
    listeningStepCheck: '检查',
    listeningPlayedTimes: '已播放 {n} 次',
    listeningSeek: '音频进度',
    listeningSpeed: '播放速度',
    listeningRestart: '从头播放',
    listeningBackSeconds: '后退 {n} 秒',
    listeningForwardSeconds: '前进 {n} 秒',
    listeningShortcutHint: '快捷键：空格播放或暂停，← → 跳转 5 秒。',
    listeningTranscriptHint: '先多听几遍，再对照原文检查。',
    listeningAnswerSheet: '答题卡',
    listeningQuestionNumber: '第 {n} 题',
    listeningUnanswered: '未作答',
    listeningReplay: '重播',
    audioPlayer: '音频播放器',
    clickToPlayOrPause: '点击播放或暂停',
    showTranscript: '显示文本',
    hideTranscript: '隐藏文本',
    transcript: '文本',
    questions: '问题',
    submitAnswers: '提交答案',
    newExercise: '新练习',
    keepPracticingListening: '继续练习以提高听力技能',
    audioNotSupported: '您的浏览器不支持音频播放',
    speakingPractice: '口语练习',
    improveYourSpeakingSkills: '提升您的口语技能',
    duration: '时长',
    tips: '提示',
    recordYourResponse: '录制您的回答',
    recording: '录制中',
    recordingComplete: '录制完成',
    clickPlayToListenOrRecordAgain: '点击播放收听或重新录制',
    clickToStartRecording: '点击麦克风开始录制',
    submitRecording: '提交录音',
    newPrompt: '新题目',
    keepPracticingSpeaking: '继续练习以提高口语技能',
    speakingChooseLevel: '选择您的级别',
    speakingChooseLevelHint: '从第 1 级开始，逐步提升',
    speakingPickShelf: '按级别筛选，再按标题选择对话课程',
    speakingChooseLesson: '选择对话课程',
    speakingLesson: '对话',
    speakingLessons: '对话',
    speakingNoLessons: '该级别没有口语课程。',
    speakingBackToLessons: '返回课程列表',
    speakingBackToLevels: '返回级别选择',
    speakingAiChat: 'AI 对话',
    speakingLines: '句',
    speakingVocabWords: '词',
    elementary: '初级',
    speakingSessionBrief: '练习简介',
    speakingPracticeWithAi: '与 AI 练习',
    speakingPracticeWithAiHint: '说话或打字回复 — AI 伙伴会回应并指导您',
    speakingStartAiConversation: '开始 AI 对话',
    speakingStarting: '正在开始…',
    speakingYou: '您',
    speakingAiPartner: 'AI 伙伴',
    speakingAiThinking: 'AI 正在思考…',
    speakingTypeReply: '输入您的回复…',
    speakingSend: '发送',
    speakingSpeak: '说话',
    speakingListening: '正在聆听…',
    speakingStartRecording: '开始录音',
    speakingStopRecording: '停止录音',
    speakingRecordIdleHint: '点“开始录音”，说出你的回答，然后再点“停止录音”。',
    speakingRecordingHint: '正在录音。说完后点“停止录音”。',
    speakingRecordingStopped: '录音已停止。请检查下面的文字，然后发送。',
    speakingRecordingEmpty: '录音已停止。没有识别到语音。请再试一次，或输入回复。',
    speakingListeningForWords: '正在听你的声音…',
    speakingMicBlocked: '麦克风权限被拒绝。请允许使用麦克风，或输入回复。',
    speakingCouldNotHear: '没有听清。请再试一次，或输入回复。',
    speakingRecordUnsupported: '此浏览器无法录制语音。请输入回复。',
    speakingOrType: '或者输入回复',
    speakingEndSession: '结束练习并获取反馈',
    speakingSaving: '正在保存…',
    speakingNewSession: '新练习',
    speakingReferenceScript: '参考脚本',
    speakingKeyVocabulary: '重点词汇',
    speakingSessionComplete: '练习完成',
    speakingSavedToAccount: '已保存到您的账户',
    speakingStrengths: '优点',
    speakingHowToImprove: '如何改进',
    speakingHowToPractice: '如何练习',
    speakingHowToPracticeStep1: '从当前级别选择一个对话。',
    speakingHowToPracticeStep2: '开始 AI 对话，通过说话或打字回复。',
    speakingHowToPracticeStep3: '聆听 AI 伙伴，需要时可参考脚本。',
    speakingHowToPracticeStep4: '结束练习以获取反馈并保存进度。',
    speakingNoConversations: '该级别暂无对话。',
    speakingStartPractice: '开始练习',
    speakingLoadingConversation: '正在加载对话…',
    speakingPerson: '角色',
    speakingConversations: '个对话',
    speakingConversation: '个对话',
    speakingLevel: '级别',
    speakingMin: '分钟',
    microphonePermissionError: '麦克风权限被拒绝。请允许访问麦克风以录制音频。',
    submitting: '提交中',
    startLesson: '开始课程',
    viewProgress: '查看进度',
    readingExercise: '阅读练习',
    readPassage: '阅读以下文章并回答问题',
    readingChooseLevel: '选择您的级别',
    readingPickShelf: '按级别筛选，然后选择课程标题开始',
    readingAllLevels: '所有级别',
    readingChooseLesson: '选择课程',
    readingLesson: '课程',
    readingLessons: '课程',
    readingStartLesson: '开始课程',
    readingNoLessons: '该级别暂无课程。',
    readingBackToLessons: '返回课程列表',
    readingMinRead: '分钟阅读',
    readingQuestionsCount: '道题',
    readingStartTimer: '开始阅读计时',
    readingPauseTimer: '暂停计时',
    readingShowHistory: '显示历史',
    readingHide: '隐藏',
    readingPassageTitle: '阅读文章',
    vocabSelectHint: '点选文章中的单词，再保存到生词本。',
    readingOpenBook: '打开书本',
    readingNoPassage: '本页暂无文章',
    readingChooseAnother: '加载另一道练习以继续',
    readingLoadNewExercise: '加载新练习',
    readingSelected: '已选：',
    readingDismiss: '关闭',
    readingComprehensionQuestions: '理解题',
    readingAnswerFromPassage: '根据文章作答——仔细扫描线索',
    readingAnswered: '已答',
    readingMultipleChoice: '选择题',
    readingMultipleChoiceDesc: '根据文章选择最佳选项。',
    readingTrueFalse: '判断题',
    readingTrueFalseDesc: '判断每个陈述是否与文章一致。',
    readingFillBlank: '填空题',
    readingFillBlankDesc: '根据文本信息填写答案。',
    readingQuestionSingular: '题',
    readingQuestionsPlural: '题',
    readingWriteAnswer: '写下您的答案...',
    readingChapterComplete: '本章完成',
    readingScoreExcellent: '太棒了——准备好进入下一级！',
    readingScoreGood: '进步不错，继续阅读！',
    readingScoreKeep: '继续练习——每一页都有价值。',
    readingBestScore: '最佳分数：',
    readingErrorLoading: '加载练习出错',
    readingAttempt: '尝试',
    readingResubmitTitle: '重新提交此练习？',
    readingResubmitTimes: '次',
    readingResubmitTime: '次',
    readingResubmitAlready: '您已经尝试过此练习',
    readingResubmitBest: '您的最佳分数是',
    readingResubmitNote: '再次提交将创建新的尝试并更新您的进度。',
    readingGoBack: '返回',
    readingSubmitAnyway: '仍然提交',
    readingTrue: '正确',
    readingFalse: '错误',
    question: '问题',
    of: '/',
    correctAnswer: '正确！',
    incorrectAnswer: '错误',
    yourScore: '您的分数',
    score: '分数',
    greatWork: '做得好！',
    writingPrompt: '写作题目',
    writingChooseLevel: '选择您的级别',
    writingPickShelf: '按级别筛选，然后选择题目开始',
    writingChooseLesson: '选择课程',
    writingLesson: '课程',
    writingLessons: '课程',
    writingStartLesson: '开始写作',
    writingNoLessons: '该级别暂无课程。',
    writingBackToLessons: '返回课程列表',
    writeResponse: '在下方写下您的回答',
    wordCount: '字数',
    minWords: '最少字数',
    submitWriting: '提交写作',
    allLevels: '全部级别',
    yourResponse: '您的回答',
    writeResponsePlaceholder: '在此写下您的回答…',
    wordsLabel: '词数',
    sentencesLabel: '句数',
    minWordsSuggested: '建议至少 {count} 词',
    showExampleAnswer: '显示示例答案',
    previousAttempts: '以往尝试',
    bestScoreLabel: '最佳分数',
    submitForGrade: '提交以评分和反馈',
    gradingWriting: '正在为您的写作评分…',
    aiReadingEssay: 'AI 正在阅读您的文章 — 可能需要最多一分钟…',
    writingSaved: '您的写作已保存到账户',
    attemptLabel: '尝试',
    yourGrade: '您的成绩',
    excellentWriting: '写得很好 — 可以进入下一课了！',
    goodWorkKeepPracticing: '做得不错。继续练习！',
    keepWritingDraft: '继续写 — 每一稿都在帮助你进步。',
    scoreFromAi: '来自 AI 老师的分数',
    scoreFromLength: '基于篇幅与结构的分数',
    teacherFeedback: '老师反馈',
    gradedByAi: '由 AI 评分',
    gradedAutomatically: '自动评分',
    rubricTask: '任务',
    rubricGrammar: '语法',
    rubricVocabulary: '词汇',
    rubricOrganization: '结构',
    strengths: '优点',
    howToImprove: '如何改进',
    grammarTips: '语法提示',
    writingResubmitTitle: '重新提交这篇写作？',
    bio: '简介',
    nativeLanguage: '母语',
    englishLevel: '英语水平',
    learningGoal: '学习目标',
    profilePicture: '头像',
    chooseAvatar: '选择头像',
    removePhoto: '移除照片',
    personalInfoSaved: '个人信息保存成功！',
    learningProfileSaved: '学习资料保存成功！',
    oopsSomethingWentWrong: '哎呀！出了点问题',
    loadingYourProfile: '正在加载您的资料...',
    thisWontTakeLong: '不会花太长时间',
    personalInformation: '个人信息',
    enterFirstName: '输入名',
    enterLastName: '输入姓',
    saving: '保存中...',
    savePersonalInformation: '保存个人信息',
    learningProfile: '学习资料',
    tellUsAboutJourney: '告诉我们您的学习之旅...',
    selectLevel: '选择级别',
    beginner: '初级',
    intermediate: '中级',
    advanced: '高级',
    egSpanish: '例如：西班牙语',
    egPassTOEFL: '例如：通过托福考试',
    dailyGoal: '每日目标',
    saveLearningProfile: '保存学习资料',
    learningProgress: '学习进度',
    setYourLearningObjective: '设定您的学习目标',
    dailyMinutes: '每日分钟',
    level: '级别',
    ofDailyGoal: '% 每日目标',
    welcomeToJourney: '欢迎开启您的语言学习之旅！设定目标并跟踪进度。',
    memberSince: '注册于',
    accountStatus: '账户状态',
    accountStatusActive: '活跃',
    accountStatusInactive: '未激活',
    userId: '用户 ID',
    profileId: '资料 ID',
    adminBadge: '管理员',
    profileAccountReadOnlyNote: '邮箱和用户名来自您的账户，无法在此页面修改。',
    nationality: '国籍',
    currentLocation: '当前所在地',
    selectCountry: '选择国家或地区',
    searchCountry: '搜索国家或地区',
    clearCountry: '清除选择',
    profilePersonalInfoHint: '更新您的姓名、国籍和当前所在地。',
    profileLearningProgressHint: '您的目标和每日学习时长。',
    profileLearningProfileHint: '自定义您在平台上的学习方式。',
    yourLanguageJourney: '您的语言之旅',
    trackProgressCelebrate: '跟踪进度、庆祝成就，在流利之路上保持动力',
    totalActivities: '总活动数',
    dayStreak: '连续天数',
    master: '精通',
    good: '良好',
    gettingStarted: '刚开始',
    completed: '已完成',
    lessonsStarted: '已开始课程',
    profileUpdates: '资料更新',
    totalLogins: '总登录次数',
    recentActivities: '最近活动',
    latestLearningMilestones: '您最新的学习里程碑和成就',
    allActivities: '所有活动',
    completedLessons: '已完成课程',
    startedLessons: '已开始课程',
    profileUpdatesFilter: '资料更新',
    logins: '登录',
    noActivitiesFound: '未找到活动',
    tryChangingFilter: '尝试更改筛选条件或开始新课程！',
    activityId: '活动 ID：',
    type: '类型',
    timestamp: '时间',
    readyForMore: '还想继续？',
    continueLearningJourney: '通过新活动和挑战继续您的学习之旅',
    exploreMoreActivities: '探索更多活动',
    pageLabel: '页',
    previous: '上一页',
    next: '下一页',
    activityTypeLessonStarted: '开始课程',
    activityTypeLessonCompleted: '完成课程',
    activityTypeProfileUpdated: '更新资料',
    activityTypeLogin: '登录',
    activityTypeWritingPractice: '写作练习',
    activityTypeReadingPractice: '阅读练习',
    activityTypeSpeakingPractice: '口语练习',
    activityTypeListeningPractice: '听力练习',
    activityTypeVocabularySaved: '已保存单词',
    activityTypeVocabularyReviewed: '已复习单词',
    activityTypeVocabularyQuiz: '词汇测验',
    vocabularyFilter: '词汇',
    activityTypeDefault: '活动',
    activityDetails: '详情',
    expandActivity: '展开',
    collapseActivity: '收起',
    settingsSubtitle: '自定义您的学习体验',
    focusArea: '重点领域',
    focusAreaDescription: '选择仪表盘应优先展示的练习方向。',
    focusAreaBalanced: '均衡',
    focusAreaBalancedHint: '自动建议最弱技能',
    focusAreaReadingHint: '在仪表盘优先阅读',
    focusAreaWritingHint: '在仪表盘优先写作',
    focusAreaListeningHint: '在仪表盘优先听力',
    focusAreaSpeakingHint: '在仪表盘优先口语',
    focusAreaGrammarHint: '在仪表盘优先语法',
    focusAreaSaved: '学习偏好已保存',
    saveDailyGoal: '保存每日目标',
    dailyGoalSettingsHint: '会在仪表盘显示为今日学习目标。',
    viewDashboard: '打开仪表盘',
    loading: '加载中…',
    preferences: '偏好设置',
    currentTheme: '当前主题',
    currentLanguage: '当前语言',
    fontSize: '字体大小',
    fontSizeDescription: '为整个应用选择舒适的阅读字号。',
    fontSizeSmall: '小',
    fontSizeMedium: '中',
    fontSizeLarge: '大',
    currentFontSize: '当前字体大小',
    preview: '预览',
    previewDescription: '此预览展示您的设置在整个应用中的显示效果。',
    home: '首页',
    navigation: '导航',
    support: '支持',
    contactUs: '联系我们',
    allRightsReserved: '保留所有权利。',
    termsAndConditions: '条款',
    footerDescription: '通过互动练习和个性化学习路径掌握英语。',
    startLearningFree: '免费开始学习',
    goToDashboard: '前往仪表盘',
    homeBadge: '受到全球学习者信赖',
    homeTitle1: '陪伴你成长的英语练习。',
    homeTitle2: '一次掌握一项技能',
    homeTitle3: '真实进步，真实成果',
    homeHeroLead: '正在练习',
    homeHeroLabelReading: '阅读 — 在每一行里看见意义',
    homeHeroLabelWriting: '写作 — 把想法写成清晰的句子',
    homeHeroLabelListening: '听力 — 捕捉每一个声音与线索',
    homeHeroLabelSpeaking: '口语 — 轻松找到自己的声音',
    homeBillboardLead: '在这里',
    homeHeroKicker: '四项技能，一段旅程',
    homeHeroLine: '在这里',
    homeHeroTo: '',
    homeBillboardReading: '阅读',
    homeBillboardWriting: '写作',
    homeBillboardListening: '听力',
    homeBillboardSpeaking: '口语',
    homeSubtitle: '你的英语进阶之旅从这里开始',
    homeHeroStory: '在掌握英语的同时探索故事的世界。发现故事。提升英语。多读。多学。自信开口。',
    homeHeroStatLessons: '课程',
    homeHeroStatSkills: '核心技能',
    homeHeroStatAnytime: '随时学习',
    homeHeroStatLevels: '学习等级',
    homeHeroStatLanguages: '语言',
    homeHeroStatFree: '即可开始',
    homeHeroStatVocab: '词汇',
    homeHeroStatExercises: '互动练习',
    homeHeroStatGrammar: '语法练习',
    homeHeroStatTopics: '学习主题',
    homeHeroStatPersonal: '个性化学习',
    homeHeroStatDaily: '每日练习',
    homeHeroStatPlatform: '一站式学习',
    homeTrust1: '永久免费',
    homeTrust2: '无需信用卡',
    homeTrust3: '30 秒即可开始',
    homeFeaturesTitle: '掌握四项核心技能',
    homeFeaturesSubtitle: '通过针对性技能发展进行全面语言学习',
    homeEverythingTitle: '你需要的，都在这里。',
    homeEverythingSubtitle: '没有多余的东西。只有让你前进的练习。',
    homeMarqueeExercises: '50+ 练习',
    homeMarqueeSkills: '4 项核心技能',
    homeMarqueeLanguages: '3 种语言',
    homeMarqueeFree: '100% 免费',
    homePickSkill: '选择技能',
    homeReadingDesc: '通过适合您级别的阅读练习，提高理解力、扩展词汇并分析文本。',
    homeWritingDesc: '通过结构化练习清晰表达想法、掌握语法并培养写作风格。',
    homeListeningDesc: '通过真实内容提升发音、理解口音并提高听力理解。',
    homeSpeakingDesc: '通过互动口语练习建立信心、练习对话并实现流利表达。',
    homeHowItWorksTitle: '如何使用',
    homeHowItWorksSubtitle: '三个简单步骤即可开始',
    homeStep1Title: '创建账户',
    homeStep1Desc: '只需邮箱，几秒钟即可注册。无需信用卡，立即开始学习。',
    homeStep2Title: '选择技能',
    homeStep2Desc: '选择阅读、写作、听力或口语。每项技能都有适合您级别的练习。',
    homeStep3Title: '开始练习',
    homeStep3Desc: '从适合您水平的练习开始。跟踪进度，每天进步。',
    homeWhyChooseTitle: '为什么选择我们',
    homeWhyChooseSubtitle: '我们平台的独特之处',
    homeBenefit1Title: '个性化学习',
    homeBenefit1Desc: '练习适应您的技能水平，确保每一步都有最佳挑战和成长。',
    homeBenefit2Title: '进度跟踪',
    homeBenefit2Desc: '通过详细分析、连续记录和全面进度报告监控您的进步。',
    homeBenefit3Title: '多语言支持',
    homeBenefit3Desc: '全面支持英语、缅甸语和中文界面，让学习更易上手、更舒适。',
    homeBenefit4Title: '即时反馈',
    homeBenefit4Desc: '每项练习立即获得结果和解释，帮助您从错误中学习。',
    homeBenefit5Title: '结构化课程',
    homeBenefit5Desc: '从初级到高级遵循清晰的学习路径，练习相互衔接。',
    homeBenefit6Title: '灵活练习',
    homeBenefit6Desc: '按自己的节奏，随时随地学习。在适合您日程的时间练习。',
    homeStat1: '互动练习',
    homeStat2: '核心技能',
    homeStat3: '语言',
    homeStat4: '免费访问',
    homeCTATitle: '准备好开始您的旅程了吗？',
    homeCTASubtitle: '加入每天提升英语的学习者。免费开始，无需信用卡。',
    homeOrbitLabel: '您的学习轨道',
    homeOrbitCenter: '4 项技能 · 1 段旅程',
    homePreviewLabel: '实时预览',
    homePreviewReading1: '探险队在日出前穿过了山谷。',
    homePreviewReading2a: '他们在黎明时分到达了',
    homePreviewReadingHighlight: '古老山脊',
    homePreviewReading2b: '。',
    homePreviewWritingDraft: 'I have working in tech since three years.',
    homePreviewWritingFix: 'I have been working in tech for three years.',
    homePreviewListening: '听录音并完成句子',
    homePreviewSpeakingQ: '用 30 秒介绍你自己。',
    homePreviewSpeakingA: '“你好，我是 Maya——我设计学习工具。”',
    homePreviewMetric: '练习准确度',
    homeExploreSkills: '练习四项技能',
    homePracticeSkill: '练习此项技能',
    nextLanguage: '下一种语言',
    errorOccurred: '发生意外错误，请重试。',
    reloadPage: '重新加载页面',
    pauseAudio: '暂停音频',
    playAudio: '播放音频',
    feedback: '反馈',
    submitSuggestion: '提交建议',
    manageFeedback: '管理反馈',
    haveSuggestion: '有建议吗？',
  }
};

const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', short: 'EN' },
  { code: 'my', name: 'ဗမာ', short: 'MM' },
  { code: 'zh', name: '中文', short: '中文' },
];

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const changeLanguage = useCallback((newLanguage) => {
    setLanguage(newLanguage);
  }, []);

  const t = useCallback((key, fallback = key) => {
    return translations[language]?.[key] || translations['en']?.[key] || fallback;
  }, [language]);

  const l = useCallback((en, my, zh) => {
    if (language === 'zh') return zh;
    if (language === 'my') return my;
    return en;
  }, [language]);

  const value = useMemo(() => ({
    language,
    changeLanguage,
    t,
    l,
    availableLanguages: AVAILABLE_LANGUAGES,
    isRTL: false,
  }), [language, changeLanguage, t, l]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}